import {DashboardSection} from './api/dashboardSection';

export type Locale = 'en' | 'fr' | 'ja' | 'pt' | 'ru';

// stored at https://docs.google.com/spreadsheets/d/1_O__dkuQTFqxr6d-KUQGVlpWOmD6smn_E3H9HBd0oy8/edit?gid=1821065006#gid=1821065006

const translations = {
  regionalReportSnapshotNdsiUnavailable: {
    en: 'Snow cover data unavailable.',
    fr: 'Données de couverture neigeuse indisponibles.',
    ja: '積雪被覆データを利用できません。',
    pt: 'Dados de cobertura de neve indisponíveis.',
    ru: 'Данные снежного покрова недоступны.',
  },
  regionalReportSnapshotUnavailable: {
    en: 'Forecast data unavailable.',
    fr: 'Données de prévision indisponibles.',
    ja: '予測データを利用できません。',
    pt: 'Dados de previsão indisponíveis.',
    ru: 'Данные прогноза недоступны.',
  },
  regionalReportEarlySnow: {
    en: 'Current snow water volume for {{site}}{{coverage}} is {{volume}}. Percentages and snowpack classifications are withheld until December 1 because early-season snow amounts are small.',
    fr: 'Le volume actuel d’eau du manteau neigeux pour {{site}}{{coverage}} est de {{volume}}. Les pourcentages et classifications sont masqués jusqu’au 1er décembre car les quantités de neige sont faibles en début de saison.',
    ja: '{{site}}{{coverage}}の現在の積雪水量は{{volume}}です。シーズン初期は積雪量が少ないため、割合と積雪状況の分類は12月1日まで表示しません。',
    pt: 'O volume atual de água na neve em {{site}}{{coverage}} é de {{volume}}. As porcentagens e classificações são omitidas até 1º de dezembro porque a quantidade de neve no início da temporada é pequena.',
    ru: 'Текущий объём воды в снежном покрове для {{site}}{{coverage}} составляет {{volume}}. Проценты и оценки снежного покрова не показываются до 1 декабря из-за малого количества снега в начале сезона.',
  },
  regionalReportSnowCoverage: {
    en: ', measured over the US part of its basin ({{coverage}}),',
    fr: ', mesuré sur la partie américaine du bassin ({{coverage}}),',
    ja: '（流域の米国側部分、{{coverage}}で測定）',
    pt: ', medido na parte americana da bacia ({{coverage}}),',
    ru: ', измеренный в американской части бассейна ({{coverage}}),',
  },
  regionalReportLtaYears: {
    en: 'The streamflow LTA uses available gauge observations before {{year}}. The actual first and last years vary by site and are not stored with the climatology.',
    fr: 'La moyenne à long terme du débit utilise les observations disponibles avant {{year}}. Les premières et dernières années réelles varient selon le site et ne sont pas enregistrées avec la climatologie.',
    ja: '流量の長期平均は{{year}}年より前の利用可能な観測値に基づきます。実際の開始年と終了年は地点ごとに異なり、平年値には記録されていません。',
    pt: 'A média de longo prazo da vazão usa as observações disponíveis anteriores a {{year}}. O primeiro e o último ano reais variam por local e não são armazenados com a climatologia.',
    ru: 'Многолетний средний расход рассчитан по доступным наблюдениям до {{year}} года. Фактические начальный и конечный годы различаются по объектам и не сохраняются с климатологией.',
  },
  regionalReportSnowSources: {
    en: 'Snow water equivalent profiles use University of Arizona SWANN over the covered US area. SNODAS time-series plots are omitted because their contributing-area query requires data for all upstream subbasins, including those in Canada.',
    fr: 'Les profils d’équivalent en eau de la neige utilisent SWANN de l’Université de l’Arizona sur la zone américaine couverte. Les séries SNODAS sont omises car leur requête exige des données pour tous les sous-bassins en amont, y compris au Canada.',
    ja: '積雪水量プロファイルには、米国内の対象範囲におけるアリゾナ大学のSWANNを使用します。SNODASの時系列は、カナダ側を含む全上流小流域のデータが必要なため省略しています。',
    pt: 'Os perfis de equivalente de água na neve usam SWANN da Universidade do Arizona na área coberta dos EUA. As séries SNODAS são omitidas porque a consulta exige dados de todas as sub-bacias a montante, inclusive no Canadá.',
    ru: 'Профили водного эквивалента снега используют SWANN Университета Аризоны в пределах покрытия в США. Графики SNODAS пропущены, поскольку запрос требует данных по всем верхним подбассейнам, включая канадские.',
  },
  regionalReportWeeklyPrecipitation: {
    en: 'Weekly precipitation — total for period',
    fr: 'Précipitations hebdomadaires — total de la période',
    ja: '週間降水量 — 期間の合計',
    pt: 'Precipitação semanal — total do período',
    ru: 'Недельные осадки — сумма за период',
  },
  regionalReportWeeklyTemperature: {
    en: 'Weekly temperature — mean for period',
    fr: 'Température hebdomadaire — moyenne de la période',
    ja: '週間気温 — 期間の平均',
    pt: 'Temperatura semanal — média do período',
    ru: 'Недельная температура — среднее за период',
  },
  regionalReportHfMean: {
    en: 'HydroForecast',
    fr: 'HydroForecast',
    ja: 'HydroForecast',
    pt: 'HydroForecast',
    ru: 'HydroForecast',
  },
  regionalReportNwrfcMean: {
    en: 'NWRFC ESP',
    fr: 'NWRFC ESP',
    ja: 'NWRFC ESP',
    pt: 'NWRFC ESP',
    ru: 'NWRFC ESP',
  },
  regionalReportLatestMeans: {
    en: 'Latest forecast means',
    fr: 'Dernières moyennes des prévisions',
    ja: '最新予測の平均値',
    pt: 'Médias das previsões mais recentes',
    ru: 'Средние значения последних прогнозов',
  },
  regionalReportEvolutionMean: {
    en: 'Mean: {{volume}} {{unit}}',
    fr: 'Moyenne : {{volume}} {{unit}}',
    ja: '平均：{{volume}} {{unit}}',
    pt: 'Média: {{volume}} {{unit}}',
    ru: 'Среднее: {{volume}} {{unit}}',
  },
  regionalReportEvolutionMeanValue: {
    en: '{{source}} mean: {{volume}} {{unit}}',
    fr: 'Moyenne {{source}} : {{volume}} {{unit}}',
    ja: '{{source}}平均：{{volume}} {{unit}}',
    pt: 'Média {{source}}: {{volume}} {{unit}}',
    ru: 'Среднее {{source}}: {{volume}} {{unit}}',
  },
  regionalReportEvolutionMarks: {
    en: 'Box: 25–75%; whiskers: 5–95%; bar: mean',
    fr: 'Boîte : 25–75 % ; moustaches : 5–95 % ; barre : moyenne',
    ja: '箱：25–75%、ひげ：5–95%、横棒：平均',
    pt: 'Caixa: 25–75%; hastes: 5–95%; barra: média',
    ru: 'Прямоугольник: 25–75%; усы: 5–95%; черта: среднее',
  },
  // PROJECTS PAGE
  projects: {
    en: 'Projects',
    fr: 'Projets',
    ja: 'プロジェクト',
    pt: 'Projetos',
    ru: 'Проекты',
  },
  projectsSites: {
    en: 'Projects & Sites',
    fr: 'Projets & Sites',
    ja: 'プロジェクト & サイト',
    pt: 'Projetos & Sites',
    ru: 'Проекты и объекты',
  },

  inputInitializationTimes: {
    en: 'Weather Inputs Initialized',
    fr: 'Entrées météo initialisées',
    ja: '天気入力初期化時間',
    pt: 'Entradas meteorológicas inicializadas',
    ru: 'Входные метеоданные инициализированы',
  },

  // HEADER / NAV
  settings: {
    en: 'Settings',
    fr: 'Paramètres',
    ja: '設定',
    pt: 'Configurações',
    ru: 'Настройки',
  },
  evaluation: {
    en: 'Evaluation',
    fr: 'Évaluation',
    ja: '評価',
    pt: 'Avaliação',
    ru: 'Оценка',
  },
  logoAlt: {
    en: 'HydroForecast Logo',
    fr: 'Logo HydroForecast',
    ja: 'ハイドロフォーキャストの商品',
    pt: 'Logotipo HydroForecast',
    ru: 'Логотип HydroForecast',
  },
  learnMore: {
    en: 'Learn more about HydroForecast',
    fr: 'En savoir plus sur HydroForecast',
    ja: 'ハイドロフォーキャストについてより詳しく知る',
    pt: 'Saiba mais sobre o HydroForecast',
    ru: 'Узнать больше о HydroForecast',
  },
  defaultUserName: {
    en: 'HydroForecast User',
    fr: 'Utilisateur HydroForecast',
    ja: 'ハイドロフォーキャストのユーザー',
    pt: 'Usuário HydroForecast',
    ru: 'Пользователь HydroForecast',
  },
  defaultOrgName: {
    en: 'My Team',
    fr: 'Mon équipe',
    ja: 'チーム',
    pt: 'Minha Equipe',
    ru: 'Моя команда',
  },
  apiDocumentation: {
    en: 'API documentation',
    fr: 'Documentation API',
    ja: 'API文書',
    pt: 'Documentação da API',
    ru: 'Документация API',
  },
  suggestions: {
    en: 'Suggestions',
    fr: 'Suggestions',
    ja: '提案',
    pt: 'Sugestões',
    ru: 'Предложения',
  },
  help: {
    en: 'Help',
    fr: 'Aide',
    ja: 'ヘルプ',
    pt: 'Ajuda',
    ru: 'Помощь',
  },
  accountMenu: {
    en: 'Account menu',
    fr: 'Menu du compte',
    ja: 'アカウントメニュー',
    pt: 'Menu da conta',
    ru: 'Меню аккаунта',
  },
  emailUs: {
    en: 'Email us',
    fr: 'Écrivez-nous',
    ja: 'メールでお問い合わせ',
    pt: 'Envie-nos um e-mail',
    ru: 'Напишите нам',
  },
  supportCenter: {
    en: 'Support Center',
    fr: "Centre d'assistance",
    ja: 'サポートセンター',
    pt: 'Central de suporte',
    ru: 'Центр поддержки',
  },
  switchOrganization: {
    en: 'Switch organization',
    fr: "Changer d'organisation",
    ja: 'プロジェクトを変更する',
    pt: 'Alterar organização',
    ru: 'Сменить организацию',
  },
  signOut: {
    en: 'Sign out',
    fr: 'Déconnexion',
    ja: 'ログアウト',
    pt: 'Sair',
    ru: 'Выйти',
  },

  // SETTINGS PAGE
  currentOrganization: {
    en: 'Current organization',
    fr: 'Organisation actuelle',
    ja: '現在のプロジェクト',
    pt: 'Organização atual',
    ru: 'Текущая организация',
  },
  notificationsAndAlerts: {
    en: 'Notifications & Alerts',
    fr: 'Notifications & Alertes',
    ja: '通知とアラート',
    pt: 'Notificações e Alertas',
    ru: 'Уведомления и оповещения',
  },
  organization: {
    en: 'Organization',
    fr: 'Organisation',
    ja: 'プロジェクト',
    pt: 'Organização',
    ru: 'Организация',
  },
  profile: {
    en: 'Profile',
    fr: 'Profil',
    ja: 'プロフィール',
    pt: 'Perfil',
    ru: 'Профиль',
  },
  profileNotInProfileOrg: {
    en: 'Switch organization to {{profileOrgName}} to edit profile',
    fr: "Changer d'organisation pour {{profileOrgName}} pour modifier le profil",
    ja: 'プロフィール組織を{{profileOrgName}}に変更してプロフィールを編集する',
    pt: 'Alterar organização para {{profileOrgName}} para editar o perfil',
    ru: 'Переключитесь на организацию {{profileOrgName}}, чтобы редактировать профиль',
  },
  team: {
    en: 'Team',
    fr: 'Équipe',
    ja: 'チーム',
    pt: 'Equipe',
    ru: 'Команда',
  },
  api: {
    en: 'API Access',
    fr: 'Accès API',
    ja: 'APIアクセス',
    pt: 'Acesso API',
    ru: 'API',
  },
  languagePreferences: {
    en: 'Language Preferences',
    fr: 'Préférences de langue',
    ja: '言語の設定',
    pt: 'Preferências de idioma',
    ru: 'Языковые настройки',
  },
  appliesOnlyToCurrentUser: {
    en: 'Applies to your account only',
    fr: "Ne s'applique qu'à votre compte",
    ja: 'あなたのアカウントにのみ適用されます',
    pt: 'Só se aplica ao seu conta',
    ru: 'Применяется только к вашей учётной записи',
  },
  chartPreferences: {
    en: 'Chart preferences',
    fr: 'Préférences des graphiques',
    ja: 'チャートの設定',
    pt: 'Preferências de gráficos',
    ru: 'Настройки графиков',
  },
  chartPreferencesDescription: {
    en: 'Choose the weather view shown by default on forecast point pages. Applies to your account only.',
    fr: "Choisissez la vue météo affichée par défaut sur les pages des points de prévision. Ne s'applique qu'à votre compte.",
    ja: '予測点ページでデフォルトで表示される天気表示を選択します。あなたのアカウントにのみ適用されます。',
    pt: 'Escolha a visualização meteorológica exibida por padrão nas páginas de pontos de previsão. Só se aplica à sua conta.',
    ru: 'Выберите, какое отображение погоды показывается по умолчанию на страницах точек прогноза. Применяется только к вашей учётной записи.',
  },
  defaultWeatherViewLabel: {
    en: 'Default weather view on forecast point pages',
    fr: 'Vue météo par défaut sur les pages des points de prévision',
    ja: '予測点ページのデフォルトの天気表示',
    pt: 'Visualização meteorológica padrão nas páginas de pontos de previsão',
    ru: 'Отображение погоды по умолчанию на страницах точек прогноза',
  },
  apiKeys: {
    en: 'API Keys',
    fr: 'Clés API',
    ja: 'APIキー',
    pt: 'Chaves API',
    ru: 'Ключи API',
  },
  apiKeyEditWarning: {
    en: 'API keys apply to your whole organization. Creating and revoking keys will affect all users.',
    fr: "Les clés API s'appliquent à l'ensemble de votre organisation. La création et la révocation des clés s'appliqueront à tous les utilisateurs.",
    ja: 'APIキーは組織全体に適用されます。キーの作成および取り消しは、すべてのユーザーに影響を与えます。',
    pt: 'As chaves API aplicam-se a toda a sua organização. A criação e a revogação de chaves aplicar-se-ão a todos.',
    ru: 'Ключи API применяются ко всей вашей организации. Создание и отзыв ключей затронут всех пользователей.',
  },
  apiUsageInfo: {
    en: 'For information on API usage, see',
    fr: "Pour des informations sur l'utilisation de l'API, voir",
    ja: 'APIの使用に関する情報については、次をご参照ください',
    pt: 'Para informações sobre o uso da API, veja',
    ru: 'Для получения информации об использовании API, см.',
  },
  documentation: {
    en: 'the documentation',
    fr: 'la documentation',
    ja: '文書',
    pt: 'a documentação',
    ru: 'документацию',
  },
  createNewKey: {
    en: 'To create a new key, contact your admin, or reach out to the Upstream Tech team at',
    fr: "Pour créer une nouvelle clé, contactez votre administrateur, ou contactez l'équipe Upstream Tech à",
    ja: '新しいキーを作成するには、管理者に連絡するか、Upstream Techチームに連絡してください',
    pt: 'Para criar uma nova chave, contate seu administrador, ou contate a equipe Upstream Tech em',
    ru: 'Чтобы создать новый ключ, свяжитесь с командой Upstream Tech по адресу',
  },
  createNewApiKey: {
    en: 'Create New API Key',
    fr: 'Créer une nouvelle clé API',
    ja: '新しいAPIキーを作成',
    pt: 'Criar nova chave API',
    ru: 'Создать новый ключ API',
  },
  noApiKeysFound: {
    en: 'No API keys found.',
    fr: 'Aucune clé API trouvée.',
    ja: 'APIキーが見つかりません。',
    pt: 'Nenhuma chave API encontrada.',
    ru: 'Ключи API не найдены.',
  },
  areYouSureYouWantToRevokeThisApiKey: {
    en: 'Are you sure you want to revoke this API key? This action cannot be undone.',
    fr: 'Êtes-vous sûr de vouloir révoquer cette clé API ? Cette action ne peut pas être annulée.',
    ja: 'このAPIキーを取り消してもよろしいですか？この操作は元に戻せません。',
    pt: 'Tem certeza que deseja revogar esta chave API? Esta ação não pode ser desfeita.',
    ru: 'Вы уверены, что хотите отозвать этот ключ API? Это действие нельзя отменить.',
  },
  createKey: {
    en: 'Create Key',
    fr: 'Créer la clé',
    ja: 'キーを作成',
    pt: 'Criar chave',
    ru: 'Создать ключ',
  },
  creating: {
    en: 'Creating...',
    fr: 'Création...',
    ja: '作成中...',
    pt: 'Criando...',
    ru: 'Создание...',
  },
  copy: {
    en: 'Copy',
    fr: 'Copier',
    ja: 'コピー',
    pt: 'Copiar',
    ru: 'Копировать',
  },
  copied: {
    en: 'Copied',
    fr: 'Copié',
    ja: 'コピー済み',
    pt: 'Copiado',
    ru: 'Скопировано',
  },
  copying: {
    en: 'Copying…',
    fr: 'Copie…',
    ja: 'コピー中…',
    pt: 'Copiando…',
    ru: 'Копирование…',
  },
  copyFailed: {
    en: 'Failed to copy image to clipboard. Try downloading the image instead.',
    fr: "Échec de la copie de l'image. Essayez de télécharger l'image à la place.",
    ja: 'クリップボードへの画像のコピーに失敗しました。代わりに画像をダウンロードしてください。',
    pt: 'Falha ao copiar a imagem para a área de transferência. Tente baixar a imagem.',
    ru: 'Не удалось скопировать изображение в буфер обмена. Попробуйте вместо этого скачать изображение.',
  },
  revoke: {
    en: 'Revoke',
    fr: 'Révoquer',
    ja: '取り消し',
    pt: 'Revogar',
    ru: 'Отозвать',
  },
  createdDateTime: {
    en: 'Created {{date}} at {{time}}',
    fr: 'Créé {{date}} à {{time}}',
    ja: '作成日時：{{date}} {{time}}',
    pt: 'Criado {{date}} às {{time}}',
    ru: 'Создан {{date}} в {{time}}',
  },
  expiration: {
    en: 'Expiration',
    fr: 'Expiration',
    ja: '有効期限',
    pt: 'Expiração',
    ru: 'Срок действия',
  },
  never: {
    en: 'Never',
    fr: 'Jamais',
    ja: 'なし',
    pt: 'Nunca',
    ru: 'Никогда',
  },
  nDays: {
    en: '{{n}} days',
    fr: '{{n}} jours',
    ja: '{{n}}日間',
    pt: '{{n}} dias',
    ru: '{{n}} дн.',
  },
  createdDateNeverExpires: {
    en: 'Created {{date}}, never expires.',
    fr: "Créé le {{date}}, n'expire jamais.",
    ja: '作成日：{{date}}、有効期限なし',
    pt: 'Criado em {{date}}, nunca expira.',
    ru: 'Создан {{date}}, бессрочный.',
  },
  createdDateExpiresOn: {
    en: 'Created {{date}}, expires {{expiresDate}}.',
    fr: 'Créé le {{date}}, expire le {{expiresDate}}.',
    ja: '作成日：{{date}}、有効期限：{{expiresDate}}',
    pt: 'Criado em {{date}}, expira em {{expiresDate}}.',
    ru: 'Создан {{date}}, истекает {{expiresDate}}.',
  },
  scope: {
    en: 'Scope',
    fr: 'Portée',
    ja: '範囲',
    pt: 'Escopo',
    ru: 'Область',
  },
  label: {
    en: 'Label',
    fr: 'Libellé',
    ja: 'ラベル',
    pt: 'Rótulo',
    ru: 'Метка',
  },
  apiKeyCreatedOnDate: {
    en: 'API key created on {{date}}',
    fr: 'Clé API créée le {{date}}',
    ja: '{{date}}に作成されたAPIキー',
    pt: 'Chave de API criada em {{date}}',
    ru: 'Ключ API создан {{date}}',
  },
  apiKeyLabelPlaceholder: {
    en: 'Optional — what is this key for?',
    fr: 'Facultatif — à quoi sert cette clé ?',
    ja: '任意 — このキーの用途',
    pt: 'Opcional — para que serve esta chave?',
    ru: 'Необязательно — для чего этот ключ?',
  },
  observations: {
    en: 'Observations',
    fr: 'Observations',
    ja: '観測',
    pt: 'Observações',
    ru: 'Наблюдения',
  },
  uploadObservations: {
    en: 'Upload Observations',
    fr: 'Télécharger les observations',
    ja: '観測をアップロード',
    pt: 'Carregar observações',
    ru: 'Загрузить наблюдения',
  },
  uploadObservationsInfo: {
    en: 'Warning: Once uploaded, data is immediately ingested into the model and will impact upcoming forecasts. For more information, see',
    fr: "Attention: Une fois téléchargées, les données sont immédiatement ingérées dans le modèle et affecteront les prévisions à venir. Pour plus d'informations, voir",
    ja: '警告: アップロード後、データはすぐにモデルに取り込まれ、今後の予測に影響を与えます。詳細については、次を参照してください',
    pt: 'Aviso: Uma vez carregadas, os dados são imediatamente ingeridos no modelo e afetarão as previsões futuras. Para mais informações, veja',
    ru: 'Предупреждение: после загрузки данные немедленно поступают в модель и повлияют на предстоящие прогнозы. Подробнее см.',
  },
  clickToSelectCsv: {
    en: 'Click to select CSV file',
    fr: 'Cliquez pour sélectionner un fichier CSV',
    ja: 'CSVファイルを選択するにはクリックしてください',
    pt: 'Clique para selecionar o arquivo CSV',
    ru: 'Нажмите, чтобы выбрать файл CSV',
  },
  remove: {
    en: 'Remove',
    fr: 'Supprimer',
    ja: '削除',
    pt: 'Remover',
    ru: 'Удалить',
  },
  upload: {
    en: 'Upload',
    fr: 'Télécharger',
    ja: 'アップロード',
    pt: 'Carregar',
    ru: 'Загрузить',
  },
  requiredColumns: {
    en: 'Required columns',
    fr: 'Colonnes requises',
    ja: '必須列',
    pt: 'Colunas obrigatórias',
    ru: 'Обязательные столбцы',
  },
  oneOrMoreOf: {
    en: 'At least one of: {{types}}',
    fr: 'Au moins un de: {{types}}',
    ja: '少なくとも1つ: {{types}}',
    pt: 'Pelo menos um de: {{types}}',
    ru: 'Хотя бы один из: {{types}}',
  },
  csvPreview: {
    en: 'CSV Preview',
    fr: 'Aperçu CSV',
    ja: 'CSVプレビュー',
    pt: 'Prévia do CSV',
    ru: 'Предпросмотр CSV',
  },
  units: {
    en: 'Units',
    fr: 'Unités',
    ja: '単位',
    pt: 'Unidades',
    ru: 'Единицы измерения',
  },
  andMoreRows: {
    en: '{{count}} more rows',
    fr: '{{count}} autres lignes',
    ja: '{{count}} 行',
    pt: '{{count}} mais linhas',
    ru: 'ещё {{count}} строк',
  },

  // Site
  trends: {
    en: 'Trends',
    fr: 'Tendances',
    ja: '傾向',
    pt: 'Tendências',
    ru: 'Тенденции',
  },
  surfaceObservations: {
    en: 'Surface observations',
    fr: 'Observations de surface',
    ja: '表面観測',
    pt: 'Observações de superfície',
    ru: 'Наземные наблюдения',
  },
  last12Months: {
    en: 'Last 12 months',
    fr: '12 derniers mois',
    ja: '過去12ヶ月',
    pt: 'Últimos 12 meses',
    ru: 'Последние 12 месяцев',
  },
  currentWaterYear: {
    en: 'Current water year',
    fr: 'Année hydrologique en cours',
    ja: '現在の水年',
    pt: 'Ano hidrológico atual',
    ru: 'Текущий водный год',
  },
  currentYear: {
    en: 'Current year',
    fr: 'Année en cours',
    ja: '今年',
    pt: 'Ano atual',
    ru: 'Текущий год',
  },
  allYears: {
    en: 'All years',
    fr: 'Toutes les années',
    ja: '1年中',
    pt: 'Todos os anos',
    ru: 'Все годы',
  },
  zoomIn: {
    en: 'Zoom in',
    fr: 'Zoomer',
    ja: 'ズームイン',
    pt: 'Aproximar',
    ru: 'Приблизить',
  },
  zoomOut: {
    en: 'Zoom out',
    fr: 'Dézoomer',
    ja: 'ズームアウト',
    pt: 'Afastar',
    ru: 'Отдалить',
  },
  basemap: {
    en: 'Basemap',
    fr: 'Fond de carte',
    ja: 'ベースマップ',
    pt: 'Mapa base',
    ru: 'Базовая карта',
  },
  basemapVector: {
    en: 'Vector',
    fr: 'Vecteur',
    ja: 'ベクター',
    pt: 'Vetorial',
    ru: 'Векторная',
  },
  basemapTerrain: {
    en: 'Terrain',
    fr: 'Relief',
    ja: '地形',
    pt: 'Relevo',
    ru: 'Рельеф',
  },
  basemapSatellite: {
    en: 'Satellite',
    fr: 'Satellite',
    ja: '衛星',
    pt: 'Satélite',
    ru: 'Спутник',
  },
  showFlowlines: {
    en: 'Show USGS NHD flowlines',
    fr: "Afficher les cours d'eau USGS NHD",
    ja: 'USGS NHD河川流路を表示',
    pt: 'Mostrar linhas de fluxo USGS NHD',
    ru: 'Показать линии водотоков USGS NHD',
  },
  hideFlowlines: {
    en: 'Hide USGS NHD flowlines',
    fr: "Masquer les cours d'eau USGS NHD",
    ja: 'USGS NHD河川流路を非表示',
    pt: 'Ocultar linhas de fluxo USGS NHD',
    ru: 'Скрыть линии водотоков USGS NHD',
  },
  flowlinesAvailability: {
    en: 'Only available in parts of the US and Canada. Send us a message if you need flowlines elsewhere.',
    fr: "Disponible uniquement dans certaines régions des États-Unis et du Canada. Contactez-nous si vous avez besoin des cours d'eau ailleurs.",
    ja: '米国とカナダの一部地域でのみ利用可能です。他の地域で河川流路が必要な場合はご連絡ください。',
    pt: 'Disponível apenas em partes dos EUA e Canadá. Envie-nos uma mensagem se precisar de linhas de fluxo em outros lugares.',
    ru: 'Доступно только в некоторых регионах США и Канады. Напишите нам, если линии водотоков нужны в других регионах.',
  },
  expandMap: {
    en: 'Expand map',
    fr: 'Agrandir la carte',
    ja: '地図を拡大',
    pt: 'Expandir mapa',
    ru: 'Развернуть карту',
  },
  collapseMap: {
    en: 'Collapse map',
    fr: 'Réduire la carte',
    ja: '地図を縮小',
    pt: 'Recolher mapa',
    ru: 'Свернуть карту',
  },
  fitToBounds: {
    en: 'Fit to bounds',
    fr: 'Ajuster aux limites',
    ja: '範囲に合わせる',
    pt: 'Ajustar aos limites',
    ru: 'По границам',
  },
  totalFlows: {
    en: 'Total flows',
    fr: 'Débits totaux',
    ja: '総流量',
    pt: 'Fluxos totais',
    ru: 'Суммарный сток',
  },
  localFlows: {
    en: 'Local flows',
    fr: 'Débits locaux',
    ja: '地域流量',
    pt: 'Fluxos locais',
    ru: 'Местный сток',
  },

  // ProjectListPage
  noProjectsMessage: {
    en: "Your team doesn't have any projects yet. Contact team@hydroforecast.com to get set up.",
    fr: "Votre équipe n'a pas encore de projets. Contactez team@hydroforecast.com pour vous installer.",
    ja: '現在、プロジェクトがありません。 team@hydroforecast.com に連絡して設定してください。',
    pt: 'Sua equipe ainda não tem projetos. Contate team@hydroforecast.com para configurar.',
    ru: 'У вашей команды пока нет проектов. Свяжитесь с team@hydroforecast.com для настройки.',
  },

  // ProjectPage
  internal: {
    en: 'Internal',
    fr: 'Interne',
    ja: '内部',
    pt: 'Interno',
    ru: 'Внутренний',
  },
  'single-site-timeseries': {
    en: 'Single-site timeseries',
    fr: 'Séries chronologiques à site unique',
    ja: '単一サイトの時系列',
    pt: 'Série temporal de local único',
    ru: 'Временной ряд одного объекта',
  },
  spatial: {
    en: 'Spatial',
    fr: 'Spatial',
    ja: '空間',
    pt: 'Espacial',
    ru: 'Пространственный',
  },

  // ShortTerm
  rate: {
    en: 'Rate',
    fr: 'Taux',
    ja: '流入量',
    pt: 'Taxa',
    ru: 'Расход',
  },
  volume: {
    en: 'Volume',
    fr: 'Volume',
    ja: '量',
    pt: 'Volume',
    ru: 'Объём',
  },
  'cumulative-volume': {
    en: 'Cumulative',
    fr: 'Cumulatif',
    ja: '累積',
    pt: 'Cumulativo',
    ru: 'Накопленный',
  },
  'cumulative-volume-from-now': {
    en: 'Cumulative from now',
    fr: 'Cumulatif depuis maintenant',
    ja: '現在からの累積',
    pt: 'Cumulativo a partir de agora',
    ru: 'Накопленный с настоящего момента',
  },
  level: {
    en: 'Level',
    fr: 'Niveau',
    ja: '水位',
    pt: 'Nível',
    ru: 'Уровень',
  },
  max: {
    en: 'Max',
    fr: 'Max',
    ja: '最大',
    pt: 'Máximo',
    ru: 'Макс',
  },
  historicalObserved: {
    en: 'Historical observed',
    fr: 'Historique observé',
    ja: '歴史的観測値',
    pt: 'Histórico observado',
    ru: 'Исторические наблюдения',
  },
  historicalRange5To95: {
    en: 'Historical 5–95%',
    fr: 'Historique 5–95 %',
    ja: '歴史的 5–95%',
    pt: 'Histórico 5–95%',
    ru: 'Исторический 5–95%',
  },
  historicalMean: {
    en: 'Historical mean',
    fr: 'Moyenne historique',
    ja: '歴史的平均値',
    pt: 'Média histórica',
    ru: 'Историческое среднее значение',
  },
  satellite: {
    en: 'Satellite',
    fr: 'Satellite',
    ja: '衛星',
    pt: 'Satélite',
    ru: 'Спутник',
  },
  observed: {
    en: 'Observed',
    fr: 'Observé',
    ja: '観測',
    pt: 'Observado',
    ru: 'Наблюдаемый',
  },
  observedClean: {
    en: 'Observed (clean)',
    fr: 'Observé (propre)',
    ja: '観測（クリーン）',
    pt: 'Observado (limpo)',
    ru: 'Наблюдаемый (очищенный)',
  },
  mean: {
    en: 'Mean',
    fr: 'Moyenne',
    ja: '平均',
    pt: 'Média',
    ru: 'Среднее',
  },
  median: {
    en: 'Median',
    fr: 'Médiane',
    ja: '中央値',
    pt: 'Mediana',
    ru: 'Медиана',
  },
  min: {
    en: 'Min',
    fr: 'Min',
    ja: '最小',
    pt: 'Mínimo',
    ru: 'Мин',
  },
  [DashboardSection.ShortTerm]: {
    en: 'Short-term',
    fr: 'Court terme',
    ja: '短期',
    pt: 'Curto prazo',
    ru: 'Краткосрочный',
  },
  last3Days: {
    en: 'Last 3 days',
    fr: 'Les 3 derniers jours',
    ja: '過去3日間',
    pt: 'Últimos 3 dias',
    ru: 'Последние 3 дня',
  },
  last10Days: {
    en: 'Last 10 days',
    fr: 'Les 10 derniers jours',
    ja: '過去10日間',
    pt: 'Últimos 10 dias',
    ru: 'Последние 10 дней',
  },
  flow: {
    en: 'Flow',
    fr: 'Débit',
    ja: '流れ',
    pt: 'Fluxo',
    ru: 'Сток',
  },
  measurement: {
    en: 'Measurement',
    fr: 'Mesure',
    ja: '測定',
    pt: 'Medição',
    ru: 'Измерение',
  },
  waterLevel: {
    en: 'Water level',
    fr: "Niveau d'eau",
    ja: '水位',
    pt: "Nível d'água",
    ru: 'Уровень воды',
  },
  weather: {
    en: 'Weather',
    fr: 'Météo',
    ja: '天気',
    pt: 'Clima',
    ru: 'Погода',
  },
  precipitation: {
    en: 'Precipitation',
    fr: 'Précipitations',
    ja: '降水量',
    pt: 'Precipitação',
    ru: 'Осадки',
  },
  temperature: {
    en: 'Temperature',
    fr: 'Température',
    ja: '温度',
    pt: 'Temperatura',
    ru: 'Температура',
  },
  vegetation: {
    en: 'Vegetation',
    fr: 'Végétation',
    ja: '植生',
    pt: 'Vegetação',
    ru: 'Растительность',
  },
  snowCover: {
    en: 'Snow cover',
    fr: 'Couverture neigeuse',
    ja: '積雪',
    pt: 'Cobertura de neve',
    ru: 'Снежный покров',
  },
  snowWaterEquivalent: {
    en: 'Snow Water Equivalent',
    fr: 'Équivalent en eau de la neige',
    ja: '雪水当量',
    pt: 'Equivalente em água da neve',
    ru: 'Водный эквивалент снега',
  },
  sweAnalysisTitle: {
    en: 'Snow Water Equivalent (SWE) analysis',
    fr: 'Analyse de l’équivalent en eau de la neige (SWE)',
    ja: '雪水当量（SWE）分析',
    pt: 'Análise do equivalente em água da neve (SWE)',
    ru: 'Анализ водного эквивалента снега (SWE)',
  },
  runSweAnalysis: {
    en: 'Run SWE Analysis',
    fr: 'Lancer l’analyse SWE',
    ja: 'SWE分析を実行',
    pt: 'Executar análise SWE',
    ru: 'Запустить анализ SWE',
  },
  runningSweAnalysis: {
    en: 'Running SWE analysis...',
    fr: 'Analyse SWE en cours...',
    ja: 'SWE分析を実行中...',
    pt: 'Executando análise SWE...',
    ru: 'Выполняется анализ SWE...',
  },
  failedToRunSweAnalysis: {
    en: 'Failed to run SWE analysis',
    fr: 'Échec de l’analyse SWE',
    ja: 'SWE分析の実行に失敗しました',
    pt: 'Falha ao executar a análise SWE',
    ru: 'Не удалось выполнить анализ SWE',
  },
  retry: {
    en: 'Retry',
    fr: 'Réessayer',
    ja: '再試行',
    pt: 'Tentar novamente',
    ru: 'Повторить',
  },
  sweVolumeByElevationBandYears: {
    en: 'SWE Volume by Elevation Band — {{numYears}} years ({{yearStart}}-{{yearEnd}})',
    fr: 'Volume SWE par tranche d’altitude — {{numYears}} ans ({{yearStart}}-{{yearEnd}})',
    ja: '標高帯別SWE体積 — {{numYears}}年（{{yearStart}}〜{{yearEnd}}）',
    pt: 'Volume de SWE por faixa de elevação — {{numYears}} anos ({{yearStart}}-{{yearEnd}})',
    ru: 'Объём SWE по высотным поясам — {{numYears}} лет ({{yearStart}}–{{yearEnd}})',
  },
  sweVolumeByElevationBandAnnual: {
    en: 'SWE Volume by Elevation Band — Annual Traces',
    fr: 'Volume SWE par tranche d’altitude — Tracés annuels',
    ja: '標高帯別SWE体積 — 年次推移',
    pt: 'Volume de SWE por faixa de elevação — Séries anuais',
    ru: 'Объём SWE по высотным поясам — годовые кривые',
  },
  sweByElevationVolume: {
    en: 'SWE Volume by Elevation Band ({{detail}})',
    fr: 'Volume SWE par tranche d’altitude ({{detail}})',
    ja: '標高帯別SWE体積（{{detail}}）',
    pt: 'Volume de SWE por faixa de elevação ({{detail}})',
    ru: 'Объём SWE по высотным поясам ({{detail}})',
  },
  sweByElevationPercentOfNormal: {
    en: 'SWE by Elevation Band — % of Normal ({{detail}})',
    fr: 'SWE par tranche d’altitude — % de la normale ({{detail}})',
    ja: '標高帯別SWE — 平年比（{{detail}}）',
    pt: 'SWE por faixa de elevação — % do normal ({{detail}})',
    ru: 'SWE по высотным поясам — % от нормы ({{detail}})',
  },
  percentOfHistoricalAvg: {
    en: '{{pct}}% of historical avg',
    fr: '{{pct}} % de la moyenne historique',
    ja: '過去平均の{{pct}}%',
    pt: '{{pct}}% da média histórica',
    ru: '{{pct}}% от исторического среднего',
  },
  sweVolumeWithUnit: {
    en: 'SWE volume ({{unit}})',
    fr: 'Volume SWE ({{unit}})',
    ja: 'SWE体積（{{unit}}）',
    pt: 'Volume de SWE ({{unit}})',
    ru: 'Объём SWE ({{unit}})',
  },
  elevationBandWithUnit: {
    en: 'Elevation band ({{unit}})',
    fr: 'Tranche d’altitude ({{unit}})',
    ja: '標高帯（{{unit}}）',
    pt: 'Faixa de elevação ({{unit}})',
    ru: 'Высотный пояс ({{unit}})',
  },
  elevationWithUnit: {
    en: 'Elevation ({{unit}})',
    fr: 'Altitude ({{unit}})',
    ja: '標高（{{unit}}）',
    pt: 'Elevação ({{unit}})',
    ru: 'Высота ({{unit}})',
  },
  interquartileRange: {
    en: 'Interquartile range (Q25–Q75)',
    fr: 'Écart interquartile (Q25–Q75)',
    ja: '四分位範囲（Q25〜Q75）',
    pt: 'Intervalo interquartil (Q25–Q75)',
    ru: 'Межквартильный размах (Q25–Q75)',
  },
  yearCurrent: {
    en: '{{year}} (current)',
    fr: '{{year}} (actuelle)',
    ja: '{{year}}年（当年）',
    pt: '{{year}} (atual)',
    ru: '{{year}} (текущий)',
  },
  highYear: {
    en: 'High: {{year}}',
    fr: 'Élevée : {{year}}',
    ja: '高水準：{{year}}',
    pt: 'Alto: {{year}}',
    ru: 'Высокий: {{year}}',
  },
  lowYear: {
    en: 'Low: {{year}}',
    fr: 'Basse : {{year}}',
    ja: '低水準：{{year}}',
    pt: 'Baixo: {{year}}',
    ru: 'Низкий: {{year}}',
  },
  current: {
    en: 'Current',
    fr: 'Actuel',
    ja: '現在',
    pt: 'Atual',
    ru: 'Текущий',
  },
  historicalAverage: {
    en: 'Historical Average',
    fr: 'Moyenne historique',
    ja: '過去平均',
    pt: 'Média histórica',
    ru: 'Историческое среднее',
  },
  noElevationDataAvailable: {
    en: 'No elevation data available',
    fr: 'Aucune donnée d’altitude disponible',
    ja: '標高データがありません',
    pt: 'Nenhum dado de elevação disponível',
    ru: 'Нет данных о высотах',
  },
  percentOfAvg: {
    en: '{{pct}}% of avg',
    fr: '{{pct}} % de la moy.',
    ja: '平均の{{pct}}%',
    pt: '{{pct}}% da média',
    ru: '{{pct}}% от среднего',
  },
  sweDataSource: {
    en: 'Data source: U. of Arizona SWANN (Snow Water Artificial Neural Network). Climatology calculated from {{yearStart}}-{{yearEnd}}.',
    fr: 'Source des données : SWANN de l’Université d’Arizona (Snow Water Artificial Neural Network). Climatologie calculée sur {{yearStart}}-{{yearEnd}}.',
    ja: 'データソース：アリゾナ大学SWANN（Snow Water Artificial Neural Network）。気候値は{{yearStart}}〜{{yearEnd}}年から算出。',
    pt: 'Fonte de dados: SWANN da U. do Arizona (Snow Water Artificial Neural Network). Climatologia calculada de {{yearStart}}-{{yearEnd}}.',
    ru: 'Источник данных: SWANN Университета Аризоны (Snow Water Artificial Neural Network). Климатология рассчитана за {{yearStart}}–{{yearEnd}}.',
  },
  spatialCoverage: {
    en: 'Spatial coverage: {{coverage}}%.',
    fr: 'Couverture spatiale : {{coverage}} %.',
    ja: '空間カバレッジ：{{coverage}}%。',
    pt: 'Cobertura espacial: {{coverage}}%.',
    ru: 'Пространственный охват: {{coverage}}%.',
  },
  save: {
    en: 'Save',
    fr: 'Enregistrer',
    ja: '保存',
    pt: 'Salvar',
    ru: 'Сохранить',
  },
  cancelChanges: {
    en: 'Cancel changes',
    fr: 'Annuler les modifications',
    ja: '変更をキャンセル',
    pt: 'Cancelar alterações',
    ru: 'Отменить изменения',
  },
  contactAdminToEdit: {
    en: 'Contact an admin to edit these settings.',
    fr: 'Contactez un administrateur pour modifier ces paramètres.',
    ja: 'これらの設定を編集するには管理者にお問い合わせください。',
    pt: 'Entre em contato com um administrador para editar estas configurações.',
    ru: 'Обратитесь к администратору, чтобы изменить эти настройки.',
  },
  invalidDate: {
    en: 'Invalid date',
    fr: 'Date invalide',
    ja: '無効な日付',
    pt: 'Data inválida',
    ru: 'Неверная дата',
  },
  startDateBeforeEndDate: {
    en: 'Start date must be before end date',
    fr: 'La date de début doit être antérieure à la date de fin',
    ja: '開始日は終了日より前である必要があります',
    pt: 'A data de início deve ser anterior à data de término',
    ru: 'Дата начала должна быть раньше даты окончания',
  },
  endDateAfterStartDate: {
    en: 'End date must be after start date',
    fr: 'La date de fin doit être postérieure à la date de début',
    ja: '終了日は開始日より後である必要があります',
    pt: 'A data de término deve ser posterior à data de início',
    ru: 'Дата окончания должна быть позже даты начала',
  },
  minimumStartDate: {
    en: 'Minimum start date: {{date}}',
    fr: 'Date de début minimale : {{date}}',
    ja: '最小開始日：{{date}}',
    pt: 'Data de início mínima: {{date}}',
    ru: 'Минимальная дата начала: {{date}}',
  },
  available: {
    en: 'Available',
    fr: 'Disponible',
    ja: '利用可能',
    pt: 'Disponível',
    ru: 'Доступно',
  },
  errorVisualizingData: {
    en: 'Error visualizing data',
    fr: 'Erreur lors de la visualisation des données',
    ja: 'データの可視化エラー',
    pt: 'Erro ao visualizar os dados',
    ru: 'Ошибка визуализации данных',
  },
  errorVisualizingDataDetail: {
    en: 'The Upstream Tech team has been notified and will reach out when this issue has been resolved.',
    fr: 'L’équipe Upstream Tech a été informée et vous contactera une fois ce problème résolu.',
    ja: 'Upstream Techチームに通知されました。この問題が解決され次第ご連絡します。',
    pt: 'A equipe da Upstream Tech foi notificada e entrará em contato assim que este problema for resolvido.',
    ru: 'Команда Upstream Tech уведомлена и свяжется с вами после устранения этой проблемы.',
  },
  showMore: {
    en: 'Show More',
    fr: 'Afficher plus',
    ja: '多く表示',
    pt: 'Mostrar mais',
    ru: 'Показать больше',
  },
  createdBy: {
    en: 'Created By {{name}}',
    fr: 'Créé par {{name}}',
    ja: '作成者：{{name}}',
    pt: 'Criado por {{name}}',
    ru: 'Создано: {{name}}',
  },
  localTime: {
    en: 'Local Time',
    fr: 'Heure locale',
    ja: '現地時間',
    pt: 'Hora local',
    ru: 'Местное время',
  },
  accountRemovedFromOrg: {
    en: 'Sorry, this account was removed from its organization. Contact your administrator to get a new invitation link.',
    fr: 'Désolé, ce compte a été retiré de son organisation. Contactez votre administrateur pour obtenir un nouveau lien d’invitation.',
    ja: '申し訳ありません。このアカウントは組織から削除されました。新しい招待リンクを取得するには管理者にお問い合わせください。',
    pt: 'Desculpe, esta conta foi removida de sua organização. Entre em contato com seu administrador para obter um novo link de convite.',
    ru: 'К сожалению, эта учётная запись была удалена из организации. Обратитесь к администратору для получения новой ссылки-приглашения.',
  },
  noOrganizationForUser: {
    en: 'No organization for this user. Contact team@hydroforecast.com and we’ll get your account set up!',
    fr: 'Aucune organisation pour cet utilisateur. Contactez team@hydroforecast.com et nous configurerons votre compte !',
    ja: 'このユーザーには組織がありません。team@hydroforecast.com までご連絡ください。アカウントを設定いたします！',
    pt: 'Nenhuma organização para este usuário. Entre em contato com team@hydroforecast.com e configuraremos sua conta!',
    ru: 'У этого пользователя нет организации. Напишите на team@hydroforecast.com, и мы настроим вашу учётную запись!',
  },
  networkRestrictedTitle: {
    en: 'Not available from this network',
    fr: 'Non disponible depuis ce réseau',
    ja: 'このネットワークからはご利用いただけません',
    pt: 'Indisponível nesta rede',
    ru: 'Недоступно из этой сети',
  },
  networkRestrictedMessage: {
    en: 'Your organization only allows access to HydroForecast from approved networks or countries, and you’re connecting from somewhere else. If you’re on a VPN, try turning it off or on. Otherwise, contact your organization’s administrator.',
    fr: 'Votre organisation n’autorise l’accès à HydroForecast que depuis des réseaux ou des pays approuvés, et vous vous connectez depuis un autre endroit. Si vous utilisez un VPN, essayez de l’activer ou de le désactiver. Sinon, contactez l’administrateur de votre organisation.',
    ja: 'お客様の組織では、承認されたネットワークまたは国からのみ HydroForecast へのアクセスが許可されていますが、現在は別の場所から接続しています。VPN をご利用の場合は、オンまたはオフに切り替えてお試しください。それ以外の場合は、組織の管理者にお問い合わせください。',
    pt: 'Sua organização só permite o acesso ao HydroForecast a partir de redes ou países aprovados, e você está se conectando de outro lugar. Se estiver usando uma VPN, tente ativá-la ou desativá-la. Caso contrário, entre em contato com o administrador da sua organização.',
    ru: 'Ваша организация разрешает доступ к HydroForecast только из одобренных сетей или стран, а вы подключаетесь из другого места. Если вы используете VPN, попробуйте включить или выключить его. В противном случае обратитесь к администратору вашей организации.',
  },
  networkRestrictedTryAgain: {
    en: 'Try again',
    fr: 'Réessayer',
    ja: '再試行',
    pt: 'Tentar novamente',
    ru: 'Повторить попытку',
  },
  networkRestrictedSwitchOrganization: {
    en: 'Switch organization',
    fr: 'Changer d’organisation',
    ja: '組織を切り替える',
    pt: 'Trocar de organização',
    ru: 'Сменить организацию',
  },
  networkRestrictedSignOut: {
    en: 'Sign out',
    fr: 'Se déconnecter',
    ja: 'サインアウト',
    pt: 'Sair',
    ru: 'Выйти',
  },
  organizations: {
    en: 'Organizations',
    fr: 'Organisations',
    ja: '組織',
    pt: 'Organizações',
    ru: 'Организации',
  },
  hourly: {
    en: 'Hourly',
    fr: 'Horaire',
    ja: '1時間あたり',
    pt: 'Por hora',
    ru: 'Почасовой',
  },
  '6-hourly': {
    en: '6-hourly',
    fr: '6 heures',
    ja: '6時間あたり',
    pt: '6 horas',
    ru: 'Каждые 6 часов',
  },
  daily: {
    en: 'Daily',
    fr: 'Quotidien',
    ja: '日単位',
    pt: 'Diário',
    ru: 'Ежедневный',
  },

  // ForecastFlowTextSummary
  forecastInProgress: {
    en: 'The latest forecast is currently being produced - check back in a few minutes.',
    fr: 'La dernière prévision est en cours de production - revenez dans quelques minutes.',
    ja: '最新の予測が現在作成されています。数分後に再度確認してください。',
    pt: 'A última previsão está sendo produzida - volte em alguns minutos.',
    ru: 'Последний прогноз в настоящее время формируется — проверьте снова через несколько минут.',
  },
  overNextDays: {
    en: 'Over the next {{horizonDays}} days:',
    fr: 'Au cours des {{horizonDays}} prochains jours :',
    ja: '次の{{horizonDays}}日間で:',
    pt: 'Nos próximos {{horizonDays}} dias:',
    ru: 'За следующие {{horizonDays}} дней:',
  },
  forecastedTo: {
    en: 'Flows are forecasted to',
    fr: 'Les débits sont prévus pour',
    ja: '流量は予測されています',
    pt: 'Os fluxos são previstos para',
    ru: 'Прогнозируется, что расход',
  },
  dischargeRange: {
    en: 'The forecasted mean discharge ranges from',
    fr: 'Le débit moyen prévu varie de',
    ja: '予測平均流量の範囲は',
    pt: 'A descarga média prevista varia de',
    ru: 'Прогнозируемый средний расход составляет от',
  },
  remainSteady: {
    en: 'remain steady',
    fr: 'rester stable',
    ja: '安定したまま',
    pt: 'permanecer estável',
    ru: 'остается стабильным',
  },
  increase: {
    en: 'increase',
    fr: 'augmenter',
    ja: '増加',
    pt: 'aumentar',
    ru: 'увеличивается',
  },
  decrease: {
    en: 'decrease',
    fr: 'diminuer',
    ja: '減少',
    pt: 'diminuir',
    ru: 'уменьшается',
  },
  forecastedToAverage: {
    en: 'Flows are forecasted to average',
    fr: 'Les débits sont prévus en moyenne',
    ja: '流量は平均で予測されています',
    pt: 'Os fluxos são previstos em média',
    ru: 'Прогнозируется, что расход составит в среднем',
  },
  pctAboveLTA: {
    en: '{{pct}}% above the long-term average',
    fr: '{{pct}}% au-dessus de la moyenne à long terme',
    ja: '長期平均を{{pct}}%上回る',
    pt: '{{pct}}% acima da média de longo prazo',
    ru: 'на {{pct}}% выше долгосрочного среднего',
  },
  pctBelowLTA: {
    en: '{{pct}}% below the long-term average',
    fr: '{{pct}}% en dessous de la moyenne à long terme',
    ja: '長期平均を{{pct}}%下回る',
    pt: '{{pct}}% abaixo da média de longo prazo',
    ru: 'на {{pct}}% ниже долгосрочного среднего',
  },
  aboutSameLTA: {
    en: 'about the same as the long-term average',
    fr: 'à peu près la même que la moyenne à long terme',
    ja: '長期平均とほぼ同じ',
    pt: 'aproximadamente o mesmo que a média de longo prazo',
    ru: 'примерно на уровне долгосрочного среднего',
  },
  peakingAround: {
    en: 'peaking around {{date}}',
    fr: 'avec un pic vers le {{date}}',
    ja: '{{date}}頃にピーク',
    pt: 'com pico por volta de {{date}}',
    ru: 'с пиком около {{date}}',
  },
  reachingLowAround: {
    en: 'reaching a low around {{date}}',
    fr: 'atteignant un minimum vers le {{date}}',
    ja: '{{date}}頃に最低点',
    pt: 'atingindo um mínimo por volta de {{date}}',
    ru: 'с минимумом около {{date}}',
  },
  andForecasted: {
    en: 'and forecasted to {{trend}}',
    fr: 'et prévus pour {{trend}}',
    ja: 'そして{{trend}}と予測されています',
    pt: 'e previstos para {{trend}}',
    ru: 'и прогнозируется {{trend}}',
  },
  higher: {
    en: 'higher',
    fr: 'plus élevé',
    ja: '高い',
    pt: 'mais alto',
    ru: 'выше',
  },
  lower: {
    en: 'lower',
    fr: 'plus bas',
    ja: '低い',
    pt: 'mais baixo',
    ru: 'ниже',
  },
  than: {
    en: 'than',
    fr: 'que',
    ja: 'より',
    pt: 'do que',
    ru: 'чем',
  },
  // ForecastPrecipitationTextSummary
  noTrendAvailable: {
    en: 'No trend available for now.',
    fr: 'Aucune tendance disponible pour le moment.',
    ja: '傾向が見られません',
    pt: 'Nenhuma tendência disponível por enquanto.',
    ru: 'Тенденции на данный момент недоступны.',
  },
  totalAccumulatedPrecipitation: {
    en: 'Total accumulated precipitation is expected to be between',
    fr: 'Les précipitations totales accumulées devraient être comprises entre',
    ja: '予想される総降水量は',
    pt: 'A precipitação total acumulada é esperada entre',
    ru: 'Ожидается, что суммарное накопленное количество осадков будет между',
  },
  and: {
    en: 'and',
    fr: 'et',
    ja: 'と',
    pt: 'e',
    ru: 'и',
  },

  // ForecastSelect
  loading: {
    en: 'Loading…',
    fr: 'Chargement…',
    ja: '読み込み中…',
    pt: 'Carregando…',
    ru: 'Загрузка…',
  },
  forecast: {
    en: 'forecast',
    fr: 'prévision',
    ja: '予測',
    pt: 'previsão',
    ru: 'прогноз',
  },
  forecasts: {
    en: 'forecasts',
    fr: 'prévisions',
    ja: '予測',
    pt: 'previsões',
    ru: 'прогнозы',
  },
  noForecastsSelected: {
    en: 'No forecasts selected',
    fr: 'Aucune prévision sélectionnée',
    ja: '予測が選択されていません',
    pt: 'Nenhuma previsão selecionada',
    ru: 'Прогнозы не выбраны',
  },
  forecastSelectionLimit: {
    en: 'Select up to {{count}} forecasts. Deselect one to choose another.',
    fr: 'Sélectionnez jusqu’à {{count}} prévisions. Désélectionnez-en une pour en choisir une autre.',
    ja: '選択できる予測は最大{{count}}件です。別の予測を選ぶには、1件の選択を解除してください。',
    pt: 'Selecione até {{count}} previsões. Desmarque uma para escolher outra.',
    ru: 'Можно выбрать до {{count}} прогнозов. Снимите выбор с одного, чтобы выбрать другой.',
  },
  noForecastsAvailable: {
    en: 'No forecasts available',
    fr: 'Aucune prévision disponible',
    ja: '利用可能な予測はありません',
    pt: 'Nenhuma previsão disponível',
    ru: 'Прогнозы недоступны',
  },
  noSupportedFlowForecast: {
    en: 'No supported flow forecast',
    fr: 'Aucune prévision de débit prise en charge',
    ja: '対応する流量予測がありません',
    pt: 'Nenhuma previsão de vazão compatível',
    ru: 'Нет поддерживаемого прогноза расхода',
  },
  forecastUnavailable: {
    en: 'Forecast data is unavailable',
    fr: 'Les données de prévision ne sont pas disponibles',
    ja: '予測データを利用できません',
    pt: 'Os dados de previsão não estão disponíveis',
    ru: 'Данные прогноза недоступны',
  },
  noForecastsInPeriod: {
    en: 'No forecasts in this period',
    fr: 'Aucune prévision pour cette période',
    ja: 'この期間の予測はありません',
    pt: 'Nenhuma previsão neste período',
    ru: 'Нет прогнозов за этот период',
  },
  forecastRequestFailed: {
    en: 'The forecast request failed.',
    fr: 'La demande de prévision a échoué.',
    ja: '予測リクエストに失敗しました。',
    pt: 'A solicitação de previsão falhou.',
    ru: 'Не удалось выполнить запрос прогноза.',
  },
  noObservationsInPeriod: {
    en: 'No observations in this period',
    fr: 'Aucune observation pour cette période',
    ja: 'この期間の観測はありません',
    pt: 'Nenhuma observação neste período',
    ru: 'Нет наблюдений за этот период',
  },
  observationRequestFailed: {
    en: 'The observation request failed.',
    fr: 'La demande d’observation a échoué.',
    ja: '観測リクエストに失敗しました。',
    pt: 'A solicitação de observação falhou.',
    ru: 'Не удалось выполнить запрос наблюдений.',
  },
  unsupportedForecastData: {
    en: 'This forecast data is not supported',
    fr: 'Ces données de prévision ne sont pas prises en charge',
    ja: 'この予測データには対応していません',
    pt: 'Estes dados de previsão não são compatíveis',
    ru: 'Эти данные прогноза не поддерживаются',
  },
  weatherRequestFailed: {
    en: 'The weather request failed.',
    fr: 'La demande météo a échoué.',
    ja: '天気リクエストに失敗しました。',
    pt: 'A solicitação de clima falhou.',
    ru: 'Не удалось выполнить запрос погоды.',
  },
  noWeatherForecastsNearSelection: {
    en: 'No {{sources}} weather forecasts within a day of the selected forecasts',
    fr: "Aucune prévision météo {{sources}} à moins d'un jour des prévisions sélectionnées",
    ja: '選択した予測の1日以内に{{sources}}の天気予報はありません',
    pt: 'Nenhuma previsão do tempo {{sources}} a menos de um dia das previsões selecionadas',
    ru: 'Нет прогнозов погоды {{sources}} в пределах суток от выбранных прогнозов',
  },
  weatherSourceUnavailable: {
    en: '{{source}} weather forecasts are unavailable: {{reason}}',
    fr: 'Les prévisions météo {{source}} ne sont pas disponibles : {{reason}}',
    ja: '{{source}}の天気予報を利用できません：{{reason}}',
    pt: 'As previsões do tempo {{source}} não estão disponíveis: {{reason}}',
    ru: 'Прогнозы погоды {{source}} недоступны: {{reason}}',
  },
  weatherObservationsUnavailable: {
    en: '{{source}} observed weather is unavailable: {{reason}}',
    fr: 'La météo observée {{source}} n’est pas disponible : {{reason}}',
    ja: '{{source}}の観測気象を利用できません：{{reason}}',
    pt: 'O tempo observado {{source}} não está disponível: {{reason}}',
    ru: 'Наблюдаемая погода {{source}} недоступна: {{reason}}',
  },
  weatherClimatologyUnavailable: {
    en: '{{source}} historical weather is unavailable: {{reason}}',
    fr: 'La météo historique {{source}} n’est pas disponible : {{reason}}',
    ja: '{{source}}の過去の気象を利用できません：{{reason}}',
    pt: 'O tempo histórico {{source}} não está disponível: {{reason}}',
    ru: 'Историческая погода {{source}} недоступна: {{reason}}',
  },
  previewH5Sections: {
    en: 'Preview H5 sections',
    fr: 'Prévisualiser les sections H5',
    ja: 'H5セクションをプレビュー',
    pt: 'Pré-visualizar seções H5',
    ru: 'Предпросмотр разделов H5',
  },
  exitH5Preview: {
    en: 'Exit H5 preview',
    fr: 'Quitter la prévisualisation H5',
    ja: 'H5プレビューを終了',
    pt: 'Sair da pré-visualização H5',
    ru: 'Выйти из предпросмотра H5',
  },
  h5PreviewNotCustomerView: {
    en: 'H5 preview — not the customer view',
    fr: 'Prévisualisation H5 — pas la vue client',
    ja: 'H5プレビュー — 顧客向け表示ではありません',
    pt: 'Pré-visualização H5 — não é a visão do cliente',
    ru: 'Предпросмотр H5 — не клиентское представление',
  },
  forecastTimesWithoutData: {
    en: 'No data in this period for the selected forecasts: {{forecasts}}',
    fr: 'Aucune donnée pour cette période pour les prévisions sélectionnées : {{forecasts}}',
    ja: '選択した予測にはこの期間のデータがありません：{{forecasts}}',
    pt: 'Nenhum dado neste período para as previsões selecionadas: {{forecasts}}',
    ru: 'Нет данных за этот период для выбранных прогнозов: {{forecasts}}',
  },
  modelForecastInitializedAt: {
    en: 'Initialized {{time}}',
    fr: 'Initialisée le {{time}}',
    ja: '初期化 {{time}}',
    pt: 'Inicializada em {{time}}',
    ru: 'Инициализирован {{time}}',
  },
  noModelForecastWithinDayOf: {
    en: 'No forecast within a day of {{time}}',
    fr: 'Aucune prévision à moins d’un jour du {{time}}',
    ja: '{{time}} の前後1日以内に予測はありません',
    pt: 'Nenhuma previsão a menos de um dia de {{time}}',
    ru: 'Нет прогноза в пределах суток от {{time}}',
  },
  addForecast: {
    en: 'Add forecast',
    fr: 'Ajouter une prévision',
    ja: '予測を追加',
    pt: 'Adicionar previsão',
    ru: 'Добавить прогноз',
  },
  moreForecastsAvailable: {
    en: '{{count}} more available',
    fr: '{{count}} de plus disponible(s)',
    ja: 'さらに {{count}} 件利用可能',
    pt: 'mais {{count}} disponível(is)',
    ru: 'ещё {{count}} доступно',
  },
  horizonDays: {
    en: '{{count}}-day',
    fr: '{{count}} jours',
    ja: '{{count}} 日間',
    pt: '{{count}} dias',
    ru: '{{count}} дн.',
  },
  removeForecast: {
    en: 'Remove forecast',
    fr: 'Retirer la prévision',
    ja: '予報を削除',
    pt: 'Remover previsão',
    ru: 'Убрать прогноз',
  },
  primaryForecastModel: {
    en: 'Primary',
    fr: 'Principal',
    ja: 'プライマリ',
    pt: 'Principal',
    ru: 'Основной',
  },
  secondaryForecastModel: {
    en: 'Supplementary',
    fr: 'Supplémentaire',
    ja: '補足',
    pt: 'Suplementar',
    ru: 'Дополнительный',
  },
  to: {
    en: 'to',
    fr: 'à',
    ja: 'に',
    pt: 'para',
    ru: 'до',
  },
  shortcuts: {
    en: 'Shortcuts',
    fr: 'Raccourcis',
    ja: 'ショートカット',
    pt: 'Atalhos',
    ru: 'Ярлыки',
  },
  allForecasts: {
    en: 'All Forecasts',
    fr: 'Toutes les prévisions',
    ja: 'すべての予測',
    pt: 'Todas as Previsões',
    ru: 'Все прогнозы',
  },
  clear: {
    en: 'Clear',
    fr: 'Effacer',
    ja: 'クリア',
    pt: 'Limpar',
    ru: 'Очистить',
  },
  notAllForecastTimesAvailable: {
    en: 'Not all forecast times are available for the selected shortcut.',
    fr: 'Tous les temps de prévision ne sont pas disponibles pour le raccourci sélectionné.',
    ja: '選択したショートカットの予測時間がすべて見られるわけではありません。',
    pt: 'Nem todos os tempos de previsão estão disponíveis para o atalho selecionado.',
    ru: 'Для выбранного ярлыка доступны не все периоды прогнозов.',
  },
  latest: {
    en: 'Latest',
    fr: 'Dernier',
    ja: '最新',
    pt: 'Último',
    ru: 'Последний',
  },
  issuedAt: {
    en: 'Issued at',
    fr: 'Émis le',
    ja: '発行',
    pt: 'Emitido em',
    ru: 'Выпущено',
  },
  firstIssuedAt: {
    en: 'First issued at',
    fr: 'Première émission',
    ja: '初回発行',
    pt: 'Primeira emissão',
    ru: 'Первый выпуск',
  },
  updatedAt: {
    en: 'Updated at',
    fr: 'Mise à jour',
    ja: '更新',
    pt: 'Atualizado em',
    ru: 'Обновлено',
  },

  // ForecastTextSummary.tsx
  generateSmartSummary: {
    en: 'Smart Summary',
    fr: 'Résumé intelligent',
    ja: 'スマートサマリー',
    pt: 'Resumo Inteligente',
    ru: 'Создать умную сводку',
  },

  // ForecastSelect forecast time tooltips
  initialized: {
    en: 'Initialized',
    fr: 'Initialisé',
    ja: '初期化',
    pt: 'Inicializado',
    ru: 'Инициализирован',
  },

  // FreezingLine
  freezing: {
    en: 'Freezing',
    fr: 'Gel',
    ja: '凍結',
    pt: 'Congelamento',
    ru: 'Замерзание',
  },

  // ThresholdLines
  thresholdCreatedByLabel: {
    en: 'Created by:',
    fr: 'Créé par :',
    ja: '作成者：',
    pt: 'Criado por:',
    ru: 'Создано:',
  },
  thresholdSourceLabel: {
    en: 'Source:',
    fr: 'Source :',
    ja: 'ソース：',
    pt: 'Fonte:',
    ru: 'Источник:',
  },
  thresholdColumnLabel: {
    en: 'Column:',
    fr: 'Colonne :',
    ja: '列：',
    pt: 'Coluna:',
    ru: 'Столбец:',
  },
  thresholdModeLabel: {
    en: 'Mode:',
    fr: 'Mode :',
    ja: 'モード：',
    pt: 'Modo:',
    ru: 'Режим:',
  },
  thresholdConditionLabel: {
    en: 'Condition:',
    fr: 'Condition :',
    ja: '条件：',
    pt: 'Condição:',
    ru: 'Условие:',
  },
  showOnChart: {
    en: 'Show on chart',
    fr: 'Afficher sur le graphique',
    ja: 'チャートに表示',
    pt: 'Mostrar no gráfico',
    ru: 'Показать на графике',
  },
  dontShow: {
    en: "Don't show",
    fr: 'Ne pas afficher',
    ja: '表示しない',
    pt: 'Não mostrar',
    ru: 'Не показывать',
  },
  showForMe: {
    en: 'Show for me',
    fr: 'Afficher pour moi',
    ja: '自分だけに表示',
    pt: 'Mostrar para mim',
    ru: 'Показывать для меня',
  },
  showForEveryone: {
    en: 'Show for everyone',
    fr: 'Afficher pour tous',
    ja: '全員に表示',
    pt: 'Mostrar para todos',
    ru: 'Показывать для всех',
  },
  showThresholdLines: {
    en: 'Show threshold lines',
    fr: 'Afficher les seuils',
    ja: 'しきい値線を表示',
    pt: 'Mostrar linhas de limite',
    ru: 'Показать линии порогов',
  },
  hideThresholdLines: {
    en: 'Hide threshold lines',
    fr: 'Masquer les seuils',
    ja: 'しきい値線を非表示',
    pt: 'Ocultar linhas de limite',
    ru: 'Скрыть линии порогов',
  },
  plotAll: {
    en: 'On chart (all)',
    fr: 'Sur le graphique (tous)',
    ja: 'チャート上 (全員)',
    pt: 'No gráfico (todos)',
    ru: 'На графике (все)',
  },
  plotMe: {
    en: 'On chart (me)',
    fr: 'Sur le graphique (moi)',
    ja: 'チャート上 (自分)',
    pt: 'No gráfico (eu)',
    ru: 'На графике (я)',
  },
  lineColor: {
    en: 'Line color',
    fr: 'Couleur de la ligne',
    ja: '線の色',
    pt: 'Cor da linha',
    ru: 'Цвет линии',
  },
  alertLabel: {
    en: 'Label',
    fr: 'Étiquette',
    ja: 'ラベル',
    pt: 'Rótulo',
    ru: 'Метка',
  },
  alertLabelPlaceholder: {
    en: 'e.g. Top of Bank',
    fr: 'ex. Sommet de la berge',
    ja: '例: 堤防天端',
    pt: 'ex. Topo da margem',
    ru: 'напр. Верх берега',
  },

  // NowLine
  now: {
    en: 'Now',
    fr: 'Maintenant',
    ja: '今',
    pt: 'Agora',
    ru: 'Сейчас',
  },

  // Seasonal
  [DashboardSection.Seasonal]: {
    en: 'Seasonal',
    fr: 'Saisonnier',
    ja: '季節',
    pt: 'Sazonal',
    ru: 'Сезонный',
  },
  [DashboardSection.Annual]: {
    en: 'Annual',
    fr: 'Annuel',
    ja: '年次',
    pt: 'Anual',
    ru: 'Годовой',
  },
  seasonal3CustomerPreview: {
    en: 'Seasonal-3 Preview 🚧',
    fr: 'Prévisualisation de Saisonnier-3 🚧',
    ja: '季節-3 プレビュー 🚧',
    pt: 'Pré-visualização de Sazonal-3 🚧',
    ru: 'Предпросмотр Seasonal-3 🚧',
  },
  seasonal3InternalPreview: {
    en: 'Seasonal-3 Preview 🤫',
    fr: 'Prévisualisation de Saisonnier-3 🤫',
    ja: '季節-3 プレビュー 🤫',
    pt: 'Pré-visualização de Sazonal-3 🤫',
    ru: 'Предпросмотр Seasonal-3 🤫',
  },
  internallyVisibleOnly: {
    en: '(internally visible only)',
    fr: '(visible uniquement à l’intérieur)',
    ja: '(内部でのみ表示可能)',
    pt: '(visível apenas internamente)',
    ru: '(видно только внутри компании)',
  },
  previewDescription: {
    en: 'Get an early look at Seasonal-3, our next-generation model. We’re in the process of upgrading your seasonal forecast, and this is a provisional preview — not yet your official production forecast. We’re excited to share it now so you can see what’s coming, and we’ll be in touch soon with more details about the transition.',
    fr: "Découvrez en avant-première Saisonnier-3, notre modèle nouvelle génération. Nous sommes en train de mettre à jour vos prévisions saisonnières, et il s'agit d'un aperçu provisoire ; il ne s'agit pas encore de vos prévisions de production officielles. Nous sommes ravis de vous le présenter dès maintenant pour que vous puissiez découvrir ce qui vous attend. Nous vous contacterons prochainement pour vous donner plus de détails sur la transition.",
    ja: '次世代モデル「季節-3」をいち早くご覧ください。現在、季節予測のアップグレードを進めており、これは暫定的なプレビューであり、正式な生産予測ではありません。今後の動向をご確認いただけるよう、今からご案内いたします。移行に関する詳細は、近日中にご連絡いたします。',
    pt: 'Dê uma vista de olhos antecipada ao Sazonal-3, o nosso modelo de próxima geração. Estamos a atualizar a vossa previsão sazonal, e esta é uma antevisão provisória — ainda não é a previsão oficial de produção. Estamos entusiasmados por partilhá-la agora para que possam ver o que está para vir, e entraremos em contacto consigo em breve com mais detalhes sobre a transição.',
    ru: 'Получите ранний доступ к Seasonal-3, нашей модели нового поколения. Мы обновляем ваш сезонный прогноз, и это предварительная версия — пока не ваш официальный рабочий прогноз. Мы рады поделиться им уже сейчас, чтобы вы могли увидеть, что вас ждёт, и вскоре свяжемся с вами с подробностями о переходе.',
  },
  previewWithoutUpgradeDescription: {
    en: "Get an early look at your new seasonal forecast. We're in the process of developing your model, and this is a provisional preview — not yet an official production forecast. We're excited to share it now so you can see what's coming, and we'll be in touch soon with more details about the rollout.",
    fr: "Découvrez en avant-première nos prévisions saisonnières. Nous sommes en train de développer notre modèle, et il s'agit d'un aperçu provisoire, et non d'une prévision officielle. Nous sommes ravis de le partager dès maintenant afin que vous puissiez avoir un aperçu des tendances à venir, et nous vous contacterons prochainement pour vous donner plus de détails sur son lancement.",
    ja: '最新の季節予報をいち早くご覧ください。現在、予報モデルの開発を進めており、これは暫定的なプレビュー版であり、正式な予報ではありません。正式なリリースに関する詳細は、追ってご連絡いたします。',
    pt: 'Confira a nova previsão sazonal. Estamos a desenvolver o nosso modelo e esta é uma antevisão provisória — ainda não é uma previsão oficial de produção. Estamos entusiasmados por partilhá-la agora para que possa ver o que está por vir, e entraremos em contacto consigo em breve com mais detalhes sobre o lançamento.',
    ru: 'Получите ранний доступ к вашему новому сезонному прогнозу. Мы разрабатываем вашу модель, и это предварительная версия — пока не официальный рабочий прогноз. Мы рады поделиться им уже сейчас, чтобы вы могли увидеть, что вас ждёт, и вскоре свяжемся с вами с подробностями о внедрении.',
  },
  seasonal2ModelInfo: {
    en: 'Model: Seasonal-2',
    fr: 'Modèle: Saisonnier-2',
    ja: 'モデル: 季節-2',
    pt: 'Modelo: Sazonal-2',
    ru: 'Модель: Seasonal-2',
  },
  latestYTD: {
    en: 'Latest YTD',
    fr: 'Dernier YTD',
    ja: '最新のYTD',
    pt: 'Último YTD',
    ru: 'Последний показатель с начала года',
  },
  latestAndLastWeek: {
    en: 'Latest & last week',
    fr: 'Dernier et la semaine dernière',
    ja: '最新と先週',
    pt: 'Último e semana passada',
    ru: 'Последний и за прошлую неделю',
  },
  every10Days: {
    en: 'Every 10 days',
    fr: 'Tous les 10 jours',
    ja: '10日ごと',
    pt: 'A cada 10 dias',
    ru: 'Каждые 10 дней',
  },
  every30Days: {
    en: 'Every 30 days',
    fr: 'Tous les 30 jours',
    ja: '30日ごと',
    pt: 'A cada 30 dias',
    ru: 'Каждые 30 дней',
  },
  '1 day': {
    en: '1 day',
    fr: '1 jour',
    ja: '1日',
    pt: '1 dia',
    ru: '1 день',
  },
  '10 day': {
    en: '10 day',
    fr: '10 jours',
    ja: '10日',
    pt: '10 dias',
    ru: '10 дней',
  },
  '30 day': {
    en: '30 day',
    fr: '30 jours',
    ja: '30日',
    pt: '30 dias',
    ru: '30 дней',
  },
  '90 day': {
    en: '90 day',
    fr: '90 jours',
    ja: '90日',
    pt: '90 dias',
    ru: '90 дней',
  },
  monthly: {
    en: 'Monthly',
    fr: 'Mensuel',
    ja: '毎月',
    pt: 'Mensal',
    ru: 'Ежемесячный',
  },
  quarterly: {
    en: 'Quarterly',
    fr: 'Trimestriel',
    ja: '四半期',
    pt: 'Trimestral',
    ru: 'Ежеквартальный',
  },
  distribution: {
    en: 'Distribution',
    fr: 'Distribution',
    ja: '分布',
    pt: 'Distribuição',
    ru: 'Распределение',
  },
  traces: {
    en: 'Traces',
    fr: 'Traces',
    ja: '記録',
    pt: 'Traços',
    ru: 'Трассировки',
  },
  sourceTraces: {
    en: 'Source Traces',
    fr: 'Tracés de débit des sources',
    ja: 'ソースに基づく流量の軌跡',
    pt: 'Traços de fluxo das fontes',
    ru: 'Трассы источников',
  },
  sourceDrivenFlows: {
    en: '{{source}}-Driven Flows',
    fr: 'Débits dérivés de {{source}}',
    ja: '{{source}}による流量',
    pt: 'Fluxos derivados do {{source}}',
    ru: 'Сток на основе источника {{source}}',
  },
  reference: {
    en: 'Reference',
    fr: 'Référence',
    ja: '参照',
    pt: 'Referência',
    ru: 'Эталон',
  },
  // SeasonalVolume
  seasonalVolume: {
    en: 'Seasonal Volume',
    fr: 'Volume saisonnier',
    ja: '季節ごとの降水量',
    pt: 'Volume Sazonal',
    ru: 'Сезонный объём',
  },
  percentLTA: {
    en: '% LTA',
    fr: '% LTA',
    ja: '% LTA',
    pt: '% LTA',
    ru: '% от среднемноголетнего',
  },
  volumePeriod: {
    en: 'Volume period',
    fr: 'Période de volume',
    ja: '降水量の期間',
    pt: 'Período de volume',
    ru: 'Период объёма',
  },
  endDateMustBeInFuture: {
    en: 'End date must be in the future.',
    fr: 'La date de fin doit être dans le futur.',
    ja: '最終日は後日でなければなりません。',
    pt: 'A data de término deve estar no futuro.',
    ru: 'Конечная дата должна быть в будущем.',
  },
  startAndEndDatesNotValid: {
    en: 'Start and end dates are not both valid.',
    fr: 'Les dates de début et de fin ne sont pas toutes deux valides。',
    ja: '開始日と最終日が両方とも有効ではありません。',
    pt: 'As datas de início e término não são ambas válidas.',
    ru: 'Начальная и конечная даты не являются действительными.',
  },
  startAndEndDateMustBeLessThanOneYearApart: {
    en: 'Start and end date must be less than 1 year apart.',
    fr: 'La date de début et de fin doit être inférieure à 1 an.',
    ja: '開始日と終了日は1年以内でなければなりません。',
    pt: 'A data de início e término deve ser inferior a 1 ano.',
    ru: 'Начальная и конечная дата должны отличаться менее чем на 1 год.',
  },
  historical: {
    en: 'Historical',
    fr: 'Historique',
    ja: '歴史的',
    pt: 'Histórico',
    ru: 'Исторический',
  },
  forecastIssueDate: {
    en: 'Forecast Issue Date',
    fr: 'Date de publication de la prévision',
    ja: '予測発行日',
    pt: 'Data de Emissão da Previsão',
    ru: 'Дата выпуска прогноза',
  },
  percentChance: {
    en: '% chance',
    fr: '% chance',
    ja: '% 確率',
    pt: '% chance',
    ru: '% вероятность',
  },
  volumeForecastChangeOverTime: {
    en: 'Volume forecast change over time',
    fr: 'Changement de prévision de volume au fil du temps',
    ja: '時間の経過に伴う降水量予測の変化',
    pt: 'Mudança de previsão de volume ao longo do tempo',
    ru: 'Изменение объёмного прогноза во времени',
  },
  chanceOfLowNormalOrHighVolume: {
    en: 'Chance of low, normal or high volume',
    fr: 'Probabilité de volume faible, normal ou élevé',
    ja: '少ない、普通、多い降水量の確率',
    pt: 'Chance de volume baixo, normal ou alto',
    ru: 'Вероятность низкого, нормального или высокого объёма',
  },
  forecastIssued: {
    en: 'Forecast issued {{date}}',
    fr: 'Prévision émise {{date}}',
    ja: '発行された予測 {{date}}',
    pt: 'Previsão emitida {{date}}',
    ru: 'Прогноз выпущен {{date}}',
  },
  categoryThresholds: {
    en: 'Thresholds (% of LTA):',
    fr: 'Seuils (% de la MLT) :',
    ja: 'しきい値（長期平均の%）：',
    pt: 'Limiares (% da MLP):',
    ru: 'Пороги (% от СДС):',
  },
  addThreshold: {
    en: 'Add threshold',
    fr: 'Ajouter un seuil',
    ja: 'しきい値を追加',
    pt: 'Adicionar limiar',
    ru: 'Добавить порог',
  },
  removeThreshold: {
    en: 'Remove threshold',
    fr: 'Supprimer le seuil',
    ja: 'しきい値を削除',
    pt: 'Remover limiar',
    ru: 'Удалить порог',
  },
  thresholdsAdjusted: {
    en: 'Adjusted — not saved as default',
    fr: 'Ajusté — non enregistré par défaut',
    ja: '調整済み — デフォルトとして未保存',
    pt: 'Ajustado — não salvo como padrão',
    ru: 'Изменено — не сохранено по умолчанию',
  },
  resetToDefault: {
    en: 'Reset',
    fr: 'Réinitialiser',
    ja: 'リセット',
    pt: 'Redefinir',
    ru: 'Сбросить',
  },
  setAsOrgDefault: {
    en: 'Set as team default',
    fr: "Définir par défaut pour l'équipe",
    ja: 'チームのデフォルトに設定',
    pt: 'Definir como padrão da equipe',
    ru: 'Сделать стандартом команды',
  },
  orgDefaultSaved: {
    en: 'Team default saved',
    fr: "Valeur par défaut de l'équipe enregistrée",
    ja: 'チームのデフォルトを保存しました',
    pt: 'Padrão da equipe salvo',
    ru: 'Стандарт команды сохранён',
  },

  // VizMoreOption
  hydroforecastIssueDate: {
    en: 'HydroForecast issue date {{date}}',
    fr: "Date d'émission HydroForecast {{date}}",
    ja: 'HydroForecast発行日 {{date}}',
    pt: 'Data de emissão HydroForecast {{date}}',
    ru: 'Дата выпуска HydroForecast {{date}}',
  },
  latestDisplayedHydroforecastIssueDate: {
    en: 'Latest displayed HydroForecast issue date {{date}}',
    fr: "Dernière date d'émission HydroForecast affichée {{date}}",
    ja: '表示最新HydroForecast発行日 {{date}}',
    pt: 'Data de emissão HydroForecast mais recente exibida {{date}}',
    ru: 'Дата последнего отображаемого выпуска HydroForecast {{date}}',
  },
  more: {
    en: 'More…',
    fr: 'Plus…',
    ja: 'さらに',
    pt: 'Mais…',
    ru: 'Ещё…',
  },
  downloadCSV: {
    en: 'Download CSV',
    fr: 'Télécharger CSV',
    ja: 'CSVをダウンロード',
    pt: 'Baixar CSV',
    ru: 'Скачать CSV',
  },
  downloadAsImage: {
    en: 'Download as image',
    fr: 'Télécharger comme image',
    ja: '画像としてダウンロード',
    pt: 'Baixar como imagem',
    ru: 'Скачать как изображение',
  },
  copyAsImage: {
    en: 'Copy as image',
    fr: 'Copier comme image',
    ja: '画像としてコピー',
    pt: 'Copiar como imagem',
    ru: 'Копировать как изображение',
  },
  openTable: {
    en: 'Open table',
    fr: 'Ouvrir le tableau',
    ja: 'テーブルの表示',
    pt: 'Abrir tabela',
    ru: 'Открыть таблицу',
  },
  viewAPIv2Queries: {
    en: 'View API v2 queries',
    fr: 'Voir les requêtes API v2',
    ja: 'API v2クエリを表示',
    pt: 'Ver consultas API v2',
    ru: 'Просмотреть запросы API v2',
  },

  organizationNameLabel: {
    en: 'Organization Name',
    fr: "Nom de l'organisation",
    ja: '組織名',
    pt: 'Nome da Organização',
    ru: 'Название организации',
  },
  mcpAccess: {
    en: 'MCP access',
    fr: 'Accès MCP',
    ja: 'MCPアクセス',
    pt: 'Acesso MCP',
    ru: 'Доступ MCP',
  },
  mcpAccessDescription: {
    en: "Control whether connected AI tools can access your organization's data.",
    fr: 'Contrôlez si les outils d’IA connectés peuvent accéder aux données de votre organisation.',
    ja: '接続されたAIツールが組織のデータにアクセスできるかどうかを管理します。',
    pt: 'Controle se ferramentas de IA conectadas podem acessar os dados da sua organização.',
    ru: 'Управляйте доступом подключённых инструментов ИИ к данным вашей организации.',
  },
  mcpAccessEnabled: {
    en: 'Enabled',
    fr: 'Activé',
    ja: '有効',
    pt: 'Ativado',
    ru: 'Включён',
  },
  mcpAccessOff: {
    en: 'Off',
    fr: 'Désactivé',
    ja: 'オフ',
    pt: 'Desativado',
    ru: 'Выключен',
  },
  mcpAccessOwnerHelp: {
    en: 'Organization owners can change this setting at any time.',
    fr: 'Les propriétaires de l’organisation peuvent modifier ce paramètre à tout moment.',
    ja: '組織のオーナーはいつでもこの設定を変更できます。',
    pt: 'Os proprietários da organização podem alterar esta configuração a qualquer momento.',
    ru: 'Владельцы организации могут изменить эту настройку в любое время.',
  },
  mcpAccessOwnerOnly: {
    en: 'Only organization owners can update this setting. Find them on the',
    fr: 'Seuls les propriétaires de l’organisation peuvent modifier ce paramètre. Retrouvez-les sur la',
    ja: 'この設定を変更できるのは組織のオーナーだけです。オーナーは次のページで確認できます：',
    pt: 'Somente os proprietários da organização podem alterar esta configuração. Encontre-os na',
    ru: 'Только владельцы организации могут изменить эту настройку. Найдите их на странице',
  },
  teamPage: {
    en: 'Team page',
    fr: 'page Équipe',
    ja: 'チームページ',
    pt: 'página da equipe',
    ru: 'команды',
  },

  inputsAndSources: {
    en: 'Inputs and sources',
    fr: 'Entrées et sources',
    ja: '入力とソース',
    pt: 'Entradas e fontes',
    ru: 'Входные данные и источники',
  },

  // ManageUsers section
  name: {
    en: 'Name',
    fr: 'Nom',
    ja: '名前',
    pt: 'Nome',
    ru: 'Имя',
  },
  status: {
    en: 'Status',
    fr: 'Statut',
    ja: 'ステータス',
    pt: 'Status',
    ru: 'Статус',
  },
  role: {
    en: 'Role',
    fr: 'Rôle',
    ja: '役割',
    pt: 'Função',
    ru: 'Роль',
  },
  description: {
    en: 'Description',
    fr: 'Description',
    ja: '説明',
    pt: 'Descrição',
    ru: 'Описание',
  },
  edit: {
    en: 'Edit',
    fr: 'Modifier',
    ja: '編集',
    pt: 'Editar',
    ru: 'Редактировать',
  },
  active: {
    en: 'Active',
    fr: 'Actif',
    ja: 'アクティブ',
    pt: 'Ativo',
    ru: 'Активный',
  },
  inviteUser: {
    en: 'Invite User',
    fr: 'Inviter un Utilisateur',
    ja: 'ユーザーを招待',
    pt: 'Convidar Utilizador',
    ru: 'Пригласить пользователя',
  },
  RoleDescriptions: {
    en: 'Role Descriptions',
    fr: 'Descriptions des Rôles',
    ja: '役割の説明',
    pt: 'Descrições de Funções',
    ru: 'Описания ролей',
  },
  emailAddress: {
    en: 'Email Address',
    fr: 'Adresse E-mail',
    ja: 'メールアドレス',
    pt: 'Endereço de E-mail',
    ru: 'Адрес электронной почты',
  },
  userDetails: {
    en: 'User Details',
    fr: "Détails de l'Utilisateur",
    ja: 'ユーザーの詳細',
    pt: 'Detalhes do Usuário',
    ru: 'Сведения о пользователе',
  },

  permissions: {
    en: 'Permissions',
    fr: 'Permissions',
    ja: '権限',
    pt: 'Permissões',
    ru: 'Разрешения',
  },
  authentication: {
    en: 'Authentication',
    fr: 'Authentification',
    ja: '認証',
    pt: 'Autenticação',
    ru: 'Аутентификация',
  },
  authenticationDescription: {
    en: 'Manage authentication settings',
    fr: "Gérer les paramètres d'authentification",
    ja: '認証設定を管理',
    pt: 'Gerenciar configurações de autenticação',
    ru: 'Управление настройками аутентификации',
  },
  configureMFA: {
    en: 'Configure Multi-Factor Authentication',
    fr: "Configurer l'authentification multifacteur",
    ja: '多要素認証を設定',
    pt: 'Configurar autenticação multifator',
    ru: 'Настроить многофакторную аутентификацию',
  },
  updateEmail: {
    en: 'Update Email',
    fr: "Mettre à jour l'e-mail",
    ja: 'メールアドレスを更新',
    pt: 'Atualizar E-mail',
    ru: 'Изменить адрес электронной почты',
  },
  confirmPromoteOwner: {
    en: 'Are you sure you want to make this teammate an owner?\n\nOwners can manage account-level settings and security decisions, so we recommend limiting this role to only the people who need it.',
    fr: 'Êtes-vous sûr de vouloir faire de ce coéquipier un Propriétaire?\n\nLes propriétaires peuvent gérer les paramètres du compte et les décisions de sécurité. Nous recommandons de limiter ce rôle aux seules personnes qui en ont besoin.',
    ja: 'このチームメイトをオーナーにしてもよろしいですか？\n\nオーナーはアカウントレベルの設定とセキュリティに関する決定を管理できるため、必要な人のみにこのロールを制限することをお勧めします。',
    pt: 'Tem certeza de que deseja tornar este membro da equipe um Proprietário?\n\nOs proprietários podem gerenciar configurações de nível de conta e decisões de segurança. Recomendamos limitar essa função apenas às pessoas que precisam dela.',
    ru: 'Вы уверены, что хотите назначить этого участника владельцем?\n\nВладельцы могут управлять настройками уровня учётной записи и решениями по безопасности, поэтому мы рекомендуем предоставлять эту роль только тем, кому она действительно нужна.',
  },
  ownerRole: {
    en: 'Owner',
    fr: 'Propriétaire',
    ja: 'オーナー',
    pt: 'Proprietário',
    ru: 'Владелец',
  },
  adminRole: {
    en: 'Admin',
    fr: 'Administrateur',
    ja: '管理者',
    pt: 'Admin',
    ru: 'Администратор',
  },
  regularRole: {
    en: 'Member',
    fr: 'Membre',
    ja: 'メンバー',
    pt: 'Membro',
    ru: 'Участник',
  },
  readonlyRole: {
    en: 'Read Only',
    fr: 'Lecture seule',
    ja: '読み取り専用',
    pt: 'Somente Leitura',
    ru: 'Только чтение',
  },
  ownerRoleDesc: {
    en: 'Manage account-level and security-related actions for all users, organization settings, and billing',
    fr: "Gérer les actions au niveau du compte et liées à la sécurité pour tous les utilisateurs, les paramètres de l'organisation et la facturation",
    ja: 'すべてのユーザーのアカウントレベルおよびセキュリティ関連のアクション、組織の設定、請求を管理します',
    pt: 'Gerenciar ações de nível de conta e relacionadas à segurança para todos os usuários, configurações da organização e cobrança',
    ru: 'Может управлять настройками организации и участниками',
  },
  adminRoleDesc: {
    en: 'Manage organization settings, invite admins, and edit profiles',
    fr: "Peut gérer les paramètres de l'organisation, inviter des administrateurs et modifier tous les profils",
    ja: '組織の設定を管理し、管理者を招待し、すべてのプロファイルを編集できます',
    pt: 'Gerenciar configurações da organização, convidar administradores e editar todos os perfis',
    ru: 'Может управлять настройками организации, приглашать администраторов и редактировать профили',
  },
  regularRoleDesc: {
    en: 'Invite members and read-only users, edit own profile',
    fr: 'Peut inviter des membres et des utilisateurs en lecture seule, modifier son propre profil',
    ja: 'メンバーと読み取り専用ユーザーを招待し、自分のプロファイルを編集できます',
    pt: 'Convidar membros e usuários somente leitura, editar o próprio perfil',
    ru: 'Может просматривать и редактировать все данные',
  },
  readonlyRoleDesc: {
    en: 'View data only',
    fr: 'Peut uniquement afficher les données',
    ja: 'データを表示するだけ',
    pt: 'Visualizar dados apenas',
    ru: 'Может только просматривать данные',
  },
  dangerZone: {
    en: 'Danger Zone',
    fr: 'Zone dangereuse',
    ja: '危険区域',
    pt: 'Zona de Perigo',
    ru: 'Опасная зона',
  },
  dangerZoneWarning: {
    en: 'Destructive actions that cannot be undone',
    fr: 'Actions destructives qui ne peuvent pas être annulées',
    ja: '一度ユーザーを削除すると、復元はできません。',
    pt: 'Ações destrutivas que não podem ser desfeitas',
    ru: 'Необратимые действия, которые нельзя отменить',
  },
  deleteUser: {
    en: 'Delete User',
    fr: "Supprimer l'utilisateur",
    ja: 'ユーザーを削除',
    pt: 'Excluir Usuário',
    ru: 'Удалить пользователя',
  },
  deleteConfirmPrompt: {
    en: 'Type {{name}} to delete this user',
    fr: 'Tapez {{name}} pour supprimer cet utilisateur',
    ja: 'このユーザーを削除するには {{name}} と入力してください',
    pt: 'Digite {{name}} para excluir este usuário',
    ru: 'Введите {{name}}, чтобы удалить этого пользователя',
  },
  cannotDeleteSelf: {
    en: 'You cannot delete yourself',
    fr: 'Vous ne pouvez pas vous supprimer',
    ja: '自分自身を削除することはできません',
    pt: 'Você não pode se excluir',
    ru: 'Вы не можете удалить себя',
  },
  resetMfa: {
    en: 'Reset MFA',
    fr: "Réinitialiser l'authentification multifacteur",
    ja: '多要素認証をリセット',
    pt: 'Redefinir autenticação multifator',
    ru: 'Сбросить MFA',
  },
  resetMfaOwnerOnly: {
    en: 'Only owners may reset multi-factor authentication',
    fr: "Seuls les propriétaires peuvent réinitialiser l'authentification multifacteur",
    ja: '多要素認証をリセットできるのはオーナーのみです',
    pt: 'Apenas proprietários podem redefinir a autenticação multifator',
    ru: 'Только владельцы могут сбрасывать многофакторную аутентификацию',
  },
  resetCredentials: {
    en: 'Log out user and reset password',
    fr: "Déconnecter l'utilisateur et réinitialiser le mot de passe",
    ja: 'ユーザーをログアウトしてパスワードをリセット',
    pt: 'Desconectar usuário e redefinir senha',
    ru: 'Выйти из учётной записи пользователя и сбросить пароль',
  },
  resetCredentialsOwnerOnly: {
    en: 'Only owners may log out users and reset passwords',
    fr: 'Seuls les propriétaires peuvent déconnecter les utilisateurs et réinitialiser les mots de passe',
    ja: 'ユーザーをログアウトしてパスワードをリセットできるのはオーナーのみです',
    pt: 'Apenas proprietários podem desconectar usuários e redefinir senhas',
    ru: 'Только владельцы могут выходить из учётных записей пользователей и сбрасывать пароли',
  },
  invite: {
    en: 'Invite',
    fr: 'Inviter',
    ja: '招待',
    pt: 'Convidar',
    ru: 'Пригласить',
  },
  inviteInfo: {
    en: 'Invite a teammate by selecting their role and entering their email address below. Invites expire after {{days}} days.',
    fr: 'Invitez un coéquipier en sélectionnant son rôle et en saisissant son adresse e-mail ci-dessous. Les invitations expirent après {{days}} jours.',
    ja: 'チームメイトを招待するには、役割を選択し、以下のメールアドレスを入力してください。招待は{{days}}日後に期限切れとなります。',
    pt: 'Convide um colega de equipa selecionando a sua função e inserindo o endereço de e-mail abaixo. Os convites expiram após {{dias}} dias.',
    ru: 'Поделитесь этими ссылками для приглашения с новыми пользователями. Каждая ссылка может использоваться только один раз.',
  },
  enterEmail: {
    en: 'Enter email',
    fr: "Entrez l'e-mail",
    ja: '電子メールアドレス',
    pt: 'Introduza o e-mail',
    ru: 'Введите адрес электронной почты',
  },
  inviteSent: {
    en: 'Invite sent to {{email}}',
    fr: 'Invitation envoyée à {{email}}',
    ja: '{{email}} に招待状を送信しました',
    pt: 'Foi enviado um convite para {{email}}',
    ru: 'Приглашение отправлено на {{email}}',
  },
  errorInvitingUser: {
    en: 'Error inviting user: {{email}}',
    fr: "Erreur lors de l'invitation de l'utilisateur : {{email}}",
    ja: 'ユーザーを招待中にエラーが発生しました: {{email}}',
    pt: 'Erro ao convidar o utilizador: {{email}}',
    ru: 'Ошибка при приглашении пользователя: {{email}}',
  },
  emailAlreadyInOrganization: {
    en: 'User {{email}} is already a member of your organization.',
    fr: "L'utilisateur {{email}} est déjà membre de votre organisation.",
    ja: 'ユーザー {{email}} はすでに組織のメンバーです。',
    pt: 'O utilizador {{email}} já é membro da sua organização.',
    ru: 'Пользователь {{email}} уже является участником вашей организации.',
  },

  // Notifications section
  newForecastNotifications: {
    en: 'New Forecast Notifications',
    fr: 'Nouvelles notifications de prévision',
    ja: '新しい予測通知',
    pt: 'Novas Notificações de Previsão',
    ru: 'Уведомления о новых прогнозах',
  },
  newForecastDescription: {
    en: 'Get notified when new forecasts are available',
    fr: 'Recevez une notification lorsque de nouvelles prévisions sont disponibles',
    ja: '新しい予測が利用可能になったときに通知を受け取る',
    pt: 'Receba notificações quando novas previsões estiverem disponíveis',
    ru: 'Получать уведомления о появлении новых прогнозов',
  },
  forecastThreshold: {
    en: 'Forecast Threshold',
    fr: 'Seuil de prévision',
    ja: '通知が発生する基準値',
    pt: 'Limite de Previsão',
    ru: 'Порог прогноза',
  },
  forecastThresholdDescription: {
    en: 'Get notified when forecasts exceed or fall below specified thresholds',
    fr: 'Recevez une notification lorsque les prévisions dépassent ou tombent en dessous des seuils spécifiés',
    ja: '予測が指定された基準値を超える、または下回った時に通知を受ける',
    pt: 'Seja notificado quando as previsões excederem ou ficarem abaixo dos limites especificados',
    ru: 'Получать уведомления, когда прогнозы превышают или опускаются ниже заданных порогов',
  },
  manageForecastAlerts: {
    en: 'View and Manage Forecast Alerts',
    fr: 'Voir et gérer les alertes de prévision',
    ja: '予報アラートを表示および管理',
    pt: 'Ver e gerenciar alertas de previsão',
    ru: 'Просмотр и управление оповещениями о прогнозах',
  },
  enrollInNewForecastNotifications: {
    en: 'Enroll in New Forecast Notifications',
    fr: "S'inscrire aux notifications de nouvelles prévisions",
    ja: '新しい予測通知に参加',
    pt: 'Inscreva-se em notificações de novas previsões',
    ru: 'Подписаться на уведомления о новых прогнозах',
  },
  showAllSites: {
    en: 'Show All Sites',
    fr: 'Afficher tous les sites',
    ja: 'すべてのサイトを表示',
    pt: 'Mostrar todos os sites',
    ru: 'Показать все объекты',
  },
  showLess: {
    en: 'Show Less',
    fr: 'Afficher moins',
    ja: '少なく表示',
    pt: 'Mostrar menos',
    ru: 'Показать меньше',
  },
  hideAlertsCreatedByOthers: {
    en: 'Hide Alerts Created By Other Users',
    fr: "Masquer les alertes créées par d'autres utilisateurs",
    ja: '他のユーザーが作成したアラートを非表示にする',
    pt: 'Ocultar alertas criados por outros usuários',
    ru: 'Скрыть оповещения, созданные другими пользователями',
  },
  showAlertsCreatedByOthers: {
    en: 'Show Alerts Created By Other Users',
    fr: "Afficher les alertes créées par d'autres utilisateurs",
    ja: '他のユーザーが作成したアラートを表示する',
    pt: 'Mostrar alertas criados por outros usuários',
    ru: 'Показать оповещения, созданные другими пользователями',
  },
  alertsCreatedByOthers: {
    en: 'Alerts Created By Other Users',
    fr: "Alertes créées par d'autres utilisateurs",
    ja: '他のユーザーが作成したアラート',
    pt: 'Alertas criados por outros usuários',
    ru: 'Оповещения, созданные другими пользователями',
  },
  createThreshold: {
    en: 'Create Threshold',
    fr: 'Créer un seuil',
    ja: 'しきい値を作成',
    pt: 'Criar Limite',
    ru: 'Создать порог',
  },
  createNewAlert: {
    en: 'Create New Alert',
    fr: 'Créer une nouvelle alerte',
    ja: '新しいアラートを作成',
    pt: 'Criar Novo Alerta',
    ru: 'Создать новое оповещение',
  },
  setUpAlert: {
    en: 'Set Up Alert',
    fr: 'Configurer une alerte',
    ja: 'アラートを設定',
    pt: 'Configurar alerta',
    ru: 'Настроить оповещение',
  },
  editAlert: {
    en: 'Edit Alert',
    fr: "Modifier l'alerte",
    ja: 'アラートを編集',
    pt: 'Editar alerta',
    ru: 'Редактировать оповещение',
  },
  forecastAlerts: {
    en: 'Forecast Alerts',
    fr: 'Alertes de prévision',
    ja: '予測アラート',
    pt: 'Alertas de Previsão',
    ru: 'Оповещения о прогнозах',
  },
  forecastAlertDescription: {
    en: 'Get alerted when forecasts meet conditions you care about — including thresholds, comparisons to other sources, and upcoming events.',
    fr: "Soyez alerté lorsque les prévisions correspondent aux conditions qui vous intéressent, notamment les seuils, les comparaisons avec d'autres sources et les événements à venir.",
    ja: 'しきい値、他のソースとの比較、今後のイベントなど、重要な条件を予測が満たした場合に通知を受け取ります。',
    pt: 'Receba alertas quando as previsões correspondem às condições que considera importantes, incluindo limites, comparações com outras fontes e eventos futuros.',
    ru: 'Получайте оповещения, когда прогнозы соответствуют важным для вас условиям — включая пороги, сравнения с другими источниками и предстоящие события.',
  },
  thresholdAlertDescription: {
    en: 'Get notified when a short-term or seasonal forecast variable crosses a threshold you set.',
    fr: "Recevez une notification lorsqu'une variable de prévision à court terme ou saisonnière franchit un seuil que vous définissez.",
    ja: '短期または季節予測変数が設定したしきい値を超えたときに通知を受け取ります。',
    pt: 'Receba notificações quando uma variável de previsão de curto prazo ou sazonal cruzar um limite que você definir.',
    ru: 'Получайте уведомления, когда краткосрочная или сезонная переменная прогноза пересекает заданный вами порог.',
  },
  ltaAlert: {
    en: 'Long-term average comparison',
    fr: 'Comparaison à la moyenne à long terme',
    ja: '長期平均との比較',
    pt: 'Comparação de média de longo prazo',
    ru: 'Сравнение с долгосрочным средним',
  },
  upcomingEventAlert: {
    en: 'Upcoming event',
    fr: 'Événement à venir',
    ja: '今後のイベント',
    pt: 'Evento futuro',
    ru: 'Предстоящее событие',
  },
  ltaAlertDescription: {
    en: 'Get notified when flow is forecasted to cross a threshold you set, based on the long-term average.',
    fr: 'Recevez une notification lorsque le débit est prévu de franchir un seuil que vous définissez, basé sur la moyenne à long terme.',
    ja: '長期平均に基づいて、流量が設定したしきい値を超えると予測された場合に通知を受け取ります。',
    pt: 'Receba notificações quando o fluxo for previsto para cruzar um limite que você definir, com base na média de longo prazo.',
    ru: 'Получайте уведомления, когда прогнозируется, что сток пересечёт заданный вами порог относительно долгосрочного среднего.',
  },
  upcomingEventAlertDescription: {
    en: 'Get notified when short-term flow is forecasted to rise sharply within the next few days.',
    fr: 'Recevez une notification lorsque le débit à court terme est prévu pour augmenter fortement dans les prochains jours.',
    ja: '短期的な流量が今後数日間に急上昇すると予測された場合に通知を受け取ります。',
    pt: 'Receba notificações quando o fluxo de curto prazo for previsto para aumentar acentuadamente nos próximos dias.',
    ru: 'Получайте уведомления, когда прогнозируется резкий рост краткосрочного стока в ближайшие несколько дней.',
  },
  learnMoreHere: {
    en: 'Learn more here',
    fr: 'En savoir plus ici',
    ja: 'ここで詳細を確認',
    pt: 'Saiba mais aqui',
    ru: 'Узнать больше',
  },
  addAlert: {
    en: 'Add Alert',
    fr: 'Ajouter une alerte',
    ja: 'アラートを追加する',
    pt: 'Adicionar um alerta',
    ru: 'Добавить оповещение',
  },
  addNewThreshold: {
    en: 'Add New Threshold',
    fr: 'Ajouter un nouveau seuil',
    ja: '新しいしきい値を追加',
    pt: 'Adicionar Novo Limite',
    ru: 'Добавить новый порог',
  },
  project: {
    en: 'Project',
    fr: 'Projet',
    ja: 'プロジェクト',
    pt: 'Projeto',
    ru: 'Проект',
  },
  site: {
    en: 'Site',
    fr: 'Site',
    ja: 'サイト',
    pt: 'Local',
    ru: 'Объект',
  },
  sites: {
    en: 'Sites',
    fr: 'Sites',
    ja: 'サイト',
    pt: 'Locais',
    ru: 'Объекты',
  },
  selectAllAvailableSites: {
    en: 'Select all available sites',
    fr: 'Sélectionner tous les sites disponibles',
    ja: '利用可能なすべてのサイトを選択',
    pt: 'Selecionar todos os sites disponíveis',
    ru: 'Выбрать все доступные объекты',
  },
  alertType: {
    en: 'Alert Type',
    fr: "Type d'alerte",
    ja: 'アラートタイプ',
    pt: 'Tipo de Alerta',
    ru: 'Тип оповещения',
  },
  source: {
    en: 'Source',
    fr: 'Source',
    ja: 'ソース',
    pt: 'Fonte',
    ru: 'Источник',
  },
  selectSource: {
    en: 'Select source',
    fr: 'Sélectionner la source',
    ja: 'ソースを選択',
    pt: 'Selecionar fonte',
    ru: 'Выбрать источник',
  },
  column: {
    en: 'Column',
    fr: 'Colonne',
    ja: '列',
    pt: 'Coluna',
    ru: 'Столбец',
  },
  selectColumn: {
    en: 'Select column',
    fr: 'Sélectionner la colonne',
    ja: '列を選択',
    pt: 'Selecionar coluna',
    ru: 'Выбрать столбец',
  },
  direction: {
    en: 'Direction',
    fr: 'Direction',
    ja: '方向',
    pt: 'Direção',
    ru: 'Направление',
  },
  isAbove: {
    en: 'is above',
    fr: 'est supérieur à',
    ja: '以上',
    pt: 'é acima de',
    ru: 'Выше',
  },
  isBelow: {
    en: 'is below',
    fr: 'est inférieur à',
    ja: '以下',
    pt: 'é abaixo de',
    ru: 'Ниже',
  },
  above: {
    en: 'Above',
    fr: 'Supérieur à',
    ja: '以上',
    pt: 'Acima de',
    ru: 'Выше',
  },
  below: {
    en: 'Below',
    fr: 'Inférieur à',
    ja: '以下',
    pt: 'Abaixo de',
    ru: 'Ниже',
  },
  projectHasNoSitesWithDataFor: {
    en: 'Project has no sites with data for',
    fr: "Le projet n'a pas de sites avec des données de",
    ja: 'プロジェクトには次のデータがありません:',
    pt: 'O projeto não tem sites com dados de',
    ru: 'В проекте нет объектов с данными для',
  },
  siteDoesNotHaveDataFor: {
    en: 'Site does not have data for',
    fr: "Le site n'a pas de données pour",
    ja: 'サイトには次のデータがありません:',
    pt: 'O site não tem dados para',
    ru: 'У объекта нет данных для',
  },
  doesNotSupportWaterLevel: {
    en: 'does not support water level alerts',
    fr: "ne prend pas en charge les alertes de niveau d'eau",
    ja: '水位アラートに対応していません',
    pt: 'não suporta alertas de nível de água',
    ru: 'не поддерживает оповещения об уровне воды',
  },
  threshold: {
    en: 'Threshold',
    fr: 'Seuil',
    ja: 'しきい値',
    pt: 'Limite',
    ru: 'Порог',
  },
  thresholdPlaceholder: {
    en: 'Enter threshold value',
    fr: 'Entrer la valeur du seuil',
    ja: 'しきい値を入力',
    pt: 'Digite o valor do limite',
    ru: 'Введите значение порога',
  },
  alertSummaryPrefix: {
    en: 'You will be notified when the forecasted value for',
    fr: 'Vous serez averti lorsque la valeur prévisionnelle de',
    ja: 'の予測値がになると通知されます。',
    pt: 'Você será notificado quando o valor previsto para',
    ru: 'Вы получите уведомление, когда прогнозируемое значение для',
  },
  examples: {
    en: 'Examples:',
    fr: 'Exemples:',
    ja: '例:',
    pt: 'Exemplos:',
    ru: 'Примеры:',
  },
  ltaExample: {
    en: '{{percent}}%: If LTA is 10 {{units}}, alert at {{threshold}} {{units}}',
    fr: '{{percent}}%: Si la moyenne à long terme est 10 {{units}}, alerte à {{threshold}} {{units}}',
    ja: '{{percent}}%: 長期平均が10 {{units}}の場合、{{threshold}} {{units}}でアラートが発生します',
    pt: '{{percent}}%: Se a média de longo prazo é 10 {{units}}, alerta em {{threshold}} {{units}}',
    ru: '{{percent}}%: если долгосрочное среднее равно 10 {{units}}, оповещать при {{threshold}} {{units}}',
  },
  orHigher: {
    en: 'or higher',
    fr: 'ou plus',
    ja: '以上',
    pt: 'ou mais',
    ru: 'или выше',
  },
  orLower: {
    en: 'or lower',
    fr: 'ou moins',
    ja: '以下',
    pt: 'ou menos',
    ru: 'или ниже',
  },
  at: {
    en: 'at',
    fr: 'à',
    ja: 'で',
    pt: 'em',
    ru: 'при',
  },
  is: {
    en: 'is',
    fr: 'est',
    ja: 'は',
    pt: 'é',
    ru: 'будет',
  },
  upcomingEventPredicate: {
    en: 'increases sharply in the coming 4 days',
    fr: 'augmente rapidement dans les prochains 4 jours',
    ja: '今後4日間で急激に増加する',
    pt: 'aumenta rapidamente nos próximos 4 dias',
    ru: 'резко возрастёт в ближайшие 4 дня',
  },
  anyOfTheAbove: {
    en: 'any of the above',
    fr: "n'importe lequel des",
    ja: '上記のいずれか',
    pt: 'qualquer um dos acima',
    ru: 'любое из вышеперечисленного',
  },
  percentLTALong: {
    en: '% of Long Term Average',
    fr: '% de la moyenne à long terme',
    ja: '長期平均の%',
    pt: '% da Média de Longo Prazo',
    ru: '% от долгосрочного среднего',
  },
  upcomingEvent: {
    en: 'Upcoming Event',
    fr: 'Événement à venir',
    ja: '今後のイベント',
    pt: 'Evento Futuro',
    ru: 'Предстоящее событие',
  },
  update: {
    en: 'Update',
    fr: 'Mettre à jour',
    ja: '更新',
    pt: 'Atualizar',
    ru: 'Обновить',
  },
  create: {
    en: 'Create',
    fr: 'Créer',
    ja: '作成',
    pt: 'Criar',
    ru: 'Создать',
  },
  delete: {
    en: 'Delete',
    fr: 'Supprimer',
    ja: '削除',
    pt: 'Excluir',
    ru: 'Удалить',
  },
  deleteConfirm: {
    en: 'Are you sure you want to delete this threshold?',
    fr: 'Êtes-vous sûr de vouloir supprimer ce seuil ?',
    ja: 'このしきい値を削除してもよろしいですか？',
    pt: 'Tem certeza de que deseja excluir este limite?',
    ru: 'Вы уверены, что хотите удалить этот порог?',
  },
  back: {
    en: 'Back',
    fr: 'Retour',
    ja: '戻る',
    pt: 'Voltar',
    ru: 'Назад',
  },
  continue: {
    en: 'Continue',
    fr: 'Continuer',
    ja: '続ける',
    pt: 'Continuar',
    ru: 'Продолжить',
  },
  submit: {
    en: 'Submit',
    fr: 'Soumettre',
    ja: '送信',
    pt: 'Enviar',
    ru: 'Отправить',
  },
  select: {
    en: 'Select',
    fr: 'Sélectionner',
    ja: '選択',
    pt: 'Selecionar',
    ru: 'Выбрать',
  },
  enable0ZForecastNotifications: {
    en: 'Enable 0Z forecast notifications',
    fr: 'Activer les notifications de prévision 0Z',
    ja: '0Z予測通知を有効にする',
    pt: 'Ativar notificações de previsão 0Z',
    ru: 'Включить уведомления о прогнозе 0Z',
  },
  enable12ZForecastNotifications: {
    en: 'Enable 12Z forecast notifications',
    fr: 'Activer les notifications de prévision 12Z',
    ja: '12Z予測通知を有効にする',
    pt: 'Ativar notificações de previsão 12Z',
    ru: 'Включить уведомления о прогнозе 12Z',
  },
  enableThresholdNotification: {
    en: 'Enable threshold notification',
    fr: 'Activer la notification de seuil',
    ja: 'しきい値通知を有効にする',
    pt: 'Ativar notificação de limite',
    ru: 'Включить уведомление о пороге',
  },
  noAlertsMessage: {
    en: 'No alerts have been created yet, click “{{createNewAlert}}” to set one up.',
    fr: "Aucune alerte n'a été créée pour le moment, cliquez sur “{{createNewAlert}}” pour en créer une.",
    ja: 'まだアラートが作成されていません。「{{createNewAlert}}」をクリックして設定してください。',
    pt: 'Nenhum alerta foi criado ainda, clique em “{{createNewAlert}}” para criar um.',
    ru: 'Оповещения ещё не созданы. Нажмите «{{createNewAlert}}», чтобы настроить.',
  },
  additionalRecipients: {
    en: 'Add additional email recipients to be copied on forecast alerts',
    fr: 'Ajouter des destinataires par e-mail supplémentaires à copier sur les alertes de prévision',
    ja: 'アラートにCCするメールアドレスを追加する',
    pt: 'Adicionar destinatários por e-mail adicionais para serem copiados nas alertas de previsão',
    ru: 'Добавьте дополнительных получателей электронной почты для копий оповещений о прогнозах',
  },
  enterEmails: {
    en: 'Enter emails',
    fr: 'Entrer les e-mails',
    ja: '同僚のメールアドレスを入力してください',
    pt: 'Digite os e-mails',
    ru: 'Введите адреса электронной почты',
  },
  pressEnterToAddEmail: {
    en: 'Press Enter to add email',
    fr: 'Appuyez sur Entrée pour ajouter un e-mail',
    ja: 'Enterキーを押してメールを追加',
    pt: 'Pressione Enter para adicionar um e-mail',
    ru: 'Нажмите Enter, чтобы добавить адрес',
  },
  invalidEmailFormat: {
    en: 'Invalid email format',
    fr: "Format d'e-mail invalide",
    ja: '無効なメール形式',
    pt: 'Formato de e-mail inválido',
    ru: 'Неверный формат адреса электронной почты',
  },
  duplicateEmail: {
    en: 'Duplicate email',
    fr: 'E-mail en double',
    ja: '重複したメール',
    pt: 'E-mail duplicado',
    ru: 'Повторяющийся адрес электронной почты',
  },
  cannotAddYourself: {
    en: 'You cannot cc yourself. The email will be sent to you.',
    fr: 'Vous ne pouvez pas vous mettre en copie. L’e-mail vous sera envoyé.',
    ja: '自分をCCすることはできません。メールは自分に送信されます。',
    pt: 'Não pode copiar a si mesmo. O e-mail será enviado para você.',
    ru: 'Вы не можете добавить себя в копию. Письмо будет отправлено вам.',
  },
  recipients: {
    en: 'Additional email recipients',
    fr: 'Destinataires par e-mail supplémentaires',
    ja: '追加のメール受取人',
    pt: 'Destinatários por e-mail adicionais',
    ru: 'Дополнительные получатели электронной почты',
  },

  // CSV Upload Errors and Status
  missingRequiredColumns: {
    en: 'Missing required columns: {{columns}}',
    fr: 'Colonnes requises manquantes : {{columns}}',
    ja: '必須列が不足しています: {{columns}}',
    pt: 'Colunas obrigatórias ausentes: {{columns}}',
    ru: 'Отсутствуют обязательные столбцы: {{columns}}',
  },
  atLeastOneColumnRequired: {
    en: 'At least one of the following columns is required: {{columns}}',
    fr: 'Au moins une des colonnes suivantes est requise : {{columns}}',
    ja: '以下の列の少なくとも1つが必要です: {{columns}}',
    pt: 'Pelo menos uma das seguintes colunas é necessária: {{columns}}',
    ru: 'Требуется хотя бы один из следующих столбцов: {{columns}}',
  },
  singleSiteIdRequired: {
    en: 'There must be only one siteId',
    fr: "Il ne doit y avoir qu'un seul siteId",
    ja: 'siteIdは1つだけである必要があります',
    pt: 'Deve haver apenas um siteId',
    ru: 'Должен быть только один siteId',
  },
  invalidDatetimeFormat: {
    en: 'Invalid datetime values found. All datetime values must include timezone offset (e.g., -04:00, +00:00, Z, etc).',
    fr: 'Valeurs de date et heure invalides trouvées. Toutes les valeurs de date et heure doivent inclure un décalage de fuseau horaire (par exemple, -04:00, +00:00, Z, etc).',
    ja: '無効な日時値が見つかりました。すべての日時値にはタイムゾーンオフセット（例：-04:00、+00:00、Zなど）を含める必要があります。',
    pt: 'Valores de data e hora inválidos encontrados. Todos os valores de data e hora devem incluir o deslocamento do fuso horário (por exemplo, -04:00, +00:00, Z, etc).',
    ru: 'Обнаружены неверные значения даты и времени. Все значения даты и времени должны включать смещение часового пояса (например, -04:00, +00:00, Z и т. д.).',
  },
  tooManyRows: {
    en: 'There must be less than 1000 rows',
    fr: 'Il doit y avoir moins de 1000 lignes',
    ja: '1000行未満である必要があります',
    pt: 'Deve haver menos de 1000 linhas',
    ru: 'Должно быть менее 1000 строк',
  },
  fileMustBeCsv: {
    en: 'File must be a CSV',
    fr: 'Le fichier doit être un CSV',
    ja: 'ファイルはCSV形式である必要があります',
    pt: 'O arquivo deve ser um CSV',
    ru: 'Файл должен быть в формате CSV',
  },
  csvFileEmpty: {
    en: 'CSV file is empty',
    fr: 'Le fichier CSV est vide',
    ja: 'CSVファイルが空です',
    pt: 'O arquivo CSV está vazio',
    ru: 'Файл CSV пуст',
  },
  failedToParseCsv: {
    en: 'Failed to parse CSV file',
    fr: "Échec de l'analyse du fichier CSV",
    ja: 'CSVファイルの解析に失敗しました',
    pt: 'Falha ao analisar o arquivo CSV',
    ru: 'Не удалось разобрать файл CSV',
  },
  uploadSuccess: {
    en: 'Observations uploaded successfully',
    fr: 'Observations téléchargées avec succès',
    ja: '観測データのアップロードが成功しました',
    pt: 'Observações carregadas com sucesso',
    ru: 'Наблюдения успешно загружены',
  },
  uploadError: {
    en: 'Error: {{message}}',
    fr: 'Erreur : {{message}}',
    ja: 'エラー: {{message}}',
    pt: 'Erro: {{message}}',
    ru: 'Ошибка: {{message}}',
  },
  forecastPoint: {
    en: 'Forecast point',
    fr: 'Point de prévision',
    ja: '予測点',
    pt: 'Ponto de previsão',
    ru: 'Точка прогноза',
  },
  basinAverage: {
    en: 'Basin average',
    fr: 'Moyenne du bassin',
    ja: '流域平均',
    pt: 'Média da bacia',
    ru: 'Среднее по бассейну',
  },
  stagingForecasts: {
    en: 'Show Staging Forecasts',
    fr: 'Afficher les prévisions de staging',
    ja: 'ステージング予測を表示',
    pt: 'Mostrar previsões de staging',
    ru: 'Показать предварительные прогнозы',
  },
};

export default translations;
